import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { announceTicketTitle } from '../utils/ticketTitleEvents';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { getUserDetailsBaseUrl, getApiBaseUrl } from '../utils/chatIdentity';
import { saveDiagnosticReport } from '../utils/diagnosticReport';
import { buildUpcomingMaintenance, MAINTENANCE_SCHEDULE } from '../utils/maintenanceSchedule';
import { toMiles, MAX_ODOMETER_MILES, ODOMETER_TOO_HIGH_MESSAGE } from '../utils/distance';
import { markPwaEngagement } from '../utils/pwaInstall';
import {
    DRIVETRAIN_OPTIONS,
    FUEL_TYPE_OPTIONS,
    NOT_PROVIDED,
    TRANSMISSION_OPTIONS,
    VEHICLE_SPEC_FIELDS,
    VIN_LOCKED_NOTE,
    applyVinSpecs,
    displaySpec,
    getVinError,
    lookupVin,
    replacedNote,
    sanitizeVinInput,
} from '../utils/vehicleSpecs';
import PartnerShopDrawer from './PartnerShopDrawer';
import GarageVehicleCard from './GarageVehicleCard';
import VehicleDetailDrawer from './VehicleDetailDrawer';
import AddDocumentWizard from './AddDocumentWizard';
import USDateInput from './USDateInput';
import {
    createMaintenanceEstimateTicket,
    formatPartnerMoney,
    formatPartnerScheduledAppointment,
    getPartnerStatusLabel,
    listPartnerRepairRequests,
} from '../utils/partnerShopApi';
import { uploadVehiclePhoto, removeVehiclePhoto } from '../utils/avatarApi';
import {
    formatTimeAgo,
    getDismissedGarageWorkStorageKey,
    getDocumentVehicleId,
    isTicketClosed,
    readLocalStringSet,
    resolveTicketDisplayTitle,
    ticketBelongsToVehicle,
    ticketPathLabel,
    ticketRealId,
    ticketThreadId,
    ticketTitle,
    ticketVehicleLabel,
    writeLocalStringSet,
} from '../utils/coreAppEnhancements';

// --- Components ---

const getVehicleLabel = (vehicle) => [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ');

const getVehicleMileageValue = (vehicle) => (
    vehicle?.odometer ?? vehicle?.total_distance_miles ?? vehicle?.mileage ?? vehicle?.miles ?? null
);

const parseMileageNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const digits = String(value).replace(/[^\d.]/g, '');
    if (!digits) return null;
    const parsed = Number(digits);
    return Number.isFinite(parsed) ? parsed : null;
};

const MILES_TO_KM = 1.609344;

// The odometer is stored in MILES. Display it in the user's preferred unit.
const formatVehicleMileage = (value, unit = 'mi') => {
    const parsed = parseMileageNumber(value);
    if (parsed !== null) {
        if (unit === 'km') {
            return `${Math.round(parsed * MILES_TO_KM).toLocaleString('en-US')} km`;
        }
        return `${Math.round(parsed).toLocaleString('en-US')} mi`;
    }
    const text = String(value || '').trim();
    return text || '';
};

const prefersReducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

// Odometer pill counts up when it first renders. Screen readers (and tests) get the final
// value straight away from the sr-only copy; only the visible digits animate.
const MILEAGE_COUNT_UP_MS = 900;

const CountUpMileage = ({ text }) => {
    const match = /^([\d,]+)(.*)$/.exec(text || '');
    const target = match ? Number(match[1].replace(/,/g, '')) : null;
    const [shown, setShown] = useState(() => (target == null || prefersReducedMotion() ? target : 0));

    useEffect(() => {
        if (target == null || prefersReducedMotion()) {
            setShown(target);
            return undefined;
        }
        let frame;
        const start = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - start) / MILEAGE_COUNT_UP_MS, 1);
            setShown(Math.round(target * (1 - Math.pow(1 - progress, 3))));
            if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target]);

    if (target == null) return text;
    return (
        <>
            <span className="sr-only">{text}</span>
            <span aria-hidden="true" className="tabular-nums">{(shown ?? target).toLocaleString('en-US')}{match[2]}</span>
        </>
    );
};

const buildVehicleAddedSummary = (vehicle = {}) => ({
    vehicle_id: vehicle?.vehicle_id || null,
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    year: vehicle?.year || '',
    vin: vehicle?.vin || '',
    plate_number: vehicle?.plate_number || '',
    mileage: vehicle?.mileage || vehicle?.miles || null,
});

const loadStoredDiagnosticReports = () => {
    if (typeof window === 'undefined') return [];

    const seen = new Set();
    const normalizeReport = (report) => {
        if (!report?.id) return null;
        const issue = report.issue || report.title || report.symptoms?.[0] || 'Symptom report';
        return {
            ...report,
            issue,
        };
    };
    const storages = [window.localStorage, window.sessionStorage].filter(Boolean);
    return storages.flatMap((storage) => {
        try {
            return Object.keys(storage).flatMap((key) => {
                if (key.startsWith('dash_diagnostic_report:')) {
                    try {
                        return [JSON.parse(storage.getItem(key))];
                    } catch {
                        return [];
                    }
                }

                if (key.startsWith('dash_chat_diagnostic_reports:')) {
                    try {
                        const cards = JSON.parse(storage.getItem(key));
                        return Array.isArray(cards)
                            ? cards.map((card) => card?.diagnosticReport).filter(Boolean)
                            : [];
                    } catch {
                        return [];
                    }
                }

                return [];
            });
        } catch {
            return [];
        }
    })
        .map(normalizeReport)
        .filter((report) => {
            if (!report?.id || seen.has(report.id)) return false;
            seen.add(report.id);
            return true;
        });
};

// Vehicle Detail Panel
// #714: known-issues JSONB can arrive as a raw JSON string ('["a", "b"]') rather than an
// array, so normalize to a clean list of strings before rendering.
const parseIssueList = (value) => {
    let list = value;
    if (typeof list === 'string') {
        const trimmed = list.trim();
        if (!trimmed) return [];
        try {
            list = JSON.parse(trimmed);
        } catch {
            list = [trimmed];
        }
    }
    if (!Array.isArray(list)) list = list ? [list] : [];
    return list
        .map((item) => (typeof item === 'string' ? item : item?.issue || item?.title || item?.name || ''))
        .map((item) => String(item).trim())
        .filter(Boolean);
};

const getKnownIssues = (insights) => {
    const llmIssues = parseIssueList(insights?.llm_known_issues);
    return llmIssues.length ? llmIssues : parseIssueList(insights?.common_issues);
};

const VehicleDetailPanel = ({ car, workplans, isOpen, onClose, onEdit, onRemove, distanceUnit = 'mi' }) => {
    const priorityColor = (p) => p === 'high' ? 'bg-rose-500' : p === 'medium' ? 'bg-amber-400' : 'bg-[#8C8C8C]';
    const priorityBadge = (p) => p === 'high' ? 'bg-rose-50 text-rose-600' : p === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-[#F9F9F9] text-[#414141]';
    
    // #380: every spec is listed; a missing value shows "Not provided".
    const specs = car ? VEHICLE_SPEC_FIELDS.map(({ key, label }) => {
        const value = key === 'mileage'
            ? formatVehicleMileage(getVehicleMileageValue(car), distanceUnit)
            : car[key];
        const shown = displaySpec(value);
        return { key, label, value: shown, missing: shown === NOT_PROVIDED };
    }) : [];
    const insightSummary = car?.model_insights?.llm_summary;
    const knownIssues = getKnownIssues(car?.model_insights);

    return (
        <div className={`fixed inset-0 z-[100] ${isOpen ? 'visible' : 'invisible pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
            />
            
            {/* Panel */}
            <div 
                className={`absolute right-0 top-0 bottom-0 w-full max-w-md md:max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out will-change-transform ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                } md:rounded-l-[32px] overflow-hidden shadow-[-20px_0_80px_rgba(0,0,0,0.1)]`}
            >
                {car && (
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 border-b border-black/5 flex items-start justify-between flex-shrink-0 bg-gradient-to-br from-[#F9F9F9] to-white">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-[#F2F2F2] border border-[#d9d9d9] rounded-2xl flex items-center justify-center text-2xl">🚗</div>
                                <div>
                                    <h2 className="text-2xl font-black text-black tracking-tight leading-none">{car.year} {car.make}</h2>
                                    <p className="text-base font-bold text-black/40 mt-0.5">{car.model} {car.trim || ''}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={onEdit} className="p-2 rounded-full hover:bg-black/5 text-black/30 hover:text-black transition-colors" title="Edit vehicle">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                                </button>
                                <button onClick={onRemove} className="p-2 rounded-full hover:bg-rose-50 text-black/30 hover:text-rose-600 transition-colors" title="Remove vehicle">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"/></svg>
                                </button>
                                <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 text-black/30 hover:text-black transition-colors">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
                                </button>
                            </div>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 custom-scrollbar">
                            {/* Specs */}
                            <section>
                                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-black/25 mb-3">Vehicle Specs</p>
                                <div className="bg-[#F9F9F9] border border-[#efefef] rounded-2xl overflow-hidden">
                                    {specs.map((s, i) => (
                                        <div key={s.key} data-testid={`spec-${s.key}`} className={`flex justify-between items-center gap-3 px-4 py-3 ${i < specs.length - 1 ? 'border-b border-black/5' : ''}` }>
                                            <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-black/30">{s.label}</span>
                                            {s.missing ? (
                                                <span className="text-xs font-semibold text-black/35">{s.value}</span>
                                            ) : (
                                                <span className={`min-w-0 break-all text-right text-xs font-black text-black bg-white border border-black/5 px-2.5 py-1 rounded-lg ${s.key === 'vin' ? 'font-mono' : ''}`}>{s.value}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {/* Work Items */}
                            <section>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-black/25">Work Items</p>
                                    {workplans.length > 0 && (
                                        <span className="text-[10px] font-black text-[#414141] bg-[#F2F2F2] px-2 py-0.5 rounded-full">{workplans.length} item{workplans.length !== 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                {workplans.length === 0 ? (
                                    <div className="bg-[#F9F9F9] border border-dashed border-[#d9d9d9] rounded-2xl px-4 py-6 text-center">
                                        <p className="text-xs font-bold text-black/30">No work items. This vehicle is all good! ✅</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {workplans.map((wp, i) => (
                                            <div key={i} className="flex items-start gap-3 p-3.5 bg-[#F9F9F9] border border-[#efefef] rounded-2xl">
                                                <div className={`flex-shrink-0 mt-1 w-2 h-2 rounded-full ${priorityColor(wp.priority)}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-black text-black">{wp.title}</p>
                                                    {wp.status && <p className="text-[10px] font-bold text-black/40 uppercase tracking-widest mt-0.5">{wp.status}</p>}
                                                </div>
                                                {wp.priority && (
                                                    <span className={`flex-shrink-0 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${priorityBadge(wp.priority)}`}>{wp.priority}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* Model Insights */}
                            {(insightSummary || knownIssues.length > 0) && (
                                <section>
                                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-black/25 mb-3">Reliability Insights</p>
                                    <div className="bg-[#F9F9F9] border border-[#efefef] rounded-2xl p-4 space-y-3">
                                        {insightSummary && (
                                            <p className="text-xs font-medium text-black/60 leading-relaxed">{insightSummary}</p>
                                        )}
                                        {knownIssues.length > 0 && (
                                            <div className={insightSummary ? 'border-t border-black/5 pt-3' : ''}>
                                                <p className="text-[9px] font-black uppercase tracking-widest text-black/30 mb-1.5">Known Issues</p>
                                                <ul className="space-y-1.5">
                                                    {knownIssues.map((issue, i) => (
                                                        <li key={i} className="flex items-start gap-2 text-xs font-medium text-black/60 leading-relaxed">
                                                            <span className="flex-shrink-0 mt-[7px] w-1 h-1 rounded-full bg-[#8C8C8C]" />
                                                            <span className="min-w-0 break-words">{issue}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// #380: Edit Vehicle covers every spec (same field names/rules as the chat Add Vehicle
// form and Vehicle Details, via utils/vehicleSpecs). Once the VIN decodes, the specs it
// provides are filled in and locked until the VIN changes; replaced values are listed.
const OPTIONAL_TEXT_FIELDS = ['trim', 'engine', 'transmission', 'drivetrain', 'fuel_type', 'plate_number'];

const toFormMileage = (vehicle, unit) => {
    const miles = parseMileageNumber(getVehicleMileageValue(vehicle));
    if (miles === null) return '';
    return String(Math.round(unit === 'km' ? miles * MILES_TO_KM : miles));
};

const buildVehicleForm = (vehicle, unit) => ({
    vehicle_id: vehicle?.vehicle_id,
    year: vehicle?.year ? String(vehicle.year) : '',
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    trim: vehicle?.trim || '',
    mileage: vehicle ? toFormMileage(vehicle, unit) : '',
    engine: vehicle?.engine || '',
    transmission: vehicle?.transmission || '',
    drivetrain: vehicle?.drivetrain || '',
    fuel_type: vehicle?.fuel_type || '',
    plate_number: vehicle?.plate_number || '',
    vin: vehicle?.vin || '',
});

const vehicleInputClass = 'w-full px-4 py-3 bg-[#F9F9F9] border border-[#d9d9d9] rounded-2xl font-bold text-black focus:border-[#D81F26] outline-none transition-all disabled:cursor-not-allowed disabled:bg-[#f1f1f1] disabled:text-black/50';

const VehicleInput = ({ label, required = false, children }) => (
    <label className="block space-y-1.5">
        <span className="block text-[10px] font-black uppercase tracking-widest text-black/40 px-1">
            {label}{required ? ' *' : ''}
        </span>
        {children}
    </label>
);

// A saved value outside the option list (e.g. older free text) stays selectable.
const SpecSelect = ({ value, options, onChange, disabled = false }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className={vehicleInputClass}>
        <option value="">Not provided</option>
        {value && !options.includes(value) && <option value={value}>{value}</option>}
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
);

const VehicleFormModal = ({ isOpen, onClose, initialData, onSave, onSaveAndLogService, phone, vehicles = [], distanceUnit = 'mi' }) => {
    const [formData, setFormData] = useState(() => buildVehicleForm(null, distanceUnit));
    const [isSaving, setIsSaving] = useState(false);
    const saveAndLogRef = useRef(false);
    const [saveError, setSaveError] = useState(null);
    const [validationMetadata, setValidationMetadata] = useState(null);
    const [isLookingUpVin, setIsLookingUpVin] = useState(false);
    const [vinNote, setVinNote] = useState('');
    const [vinLockedFields, setVinLockedFields] = useState(() => new Set());
    const [vinReplaced, setVinReplaced] = useState([]);
    const [confirmVinChange, setConfirmVinChange] = useState(false);
    const autoLookupVinRef = useRef('');

    useEffect(() => {
        if (isOpen) {
            fetch(`${getUserDetailsBaseUrl()}/api/validation_metadata`)
                .then(res => res.json())
                .then(data => setValidationMetadata(data))
                .catch(err => console.warn('Failed to load validation metadata:', err));
        }
    }, [isOpen]);

    useEffect(() => {
        setSaveError(null);
        setVinNote('');
        setVinLockedFields(new Set());
        setVinReplaced([]);
        setConfirmVinChange(false);
        autoLookupVinRef.current = '';
        setFormData(buildVehicleForm(initialData, distanceUnit));
    }, [initialData, isOpen, distanceUnit]);

    // A saved VIN (on open) or a newly typed complete VIN is looked up automatically, so
    // the VIN's specs are filled in and locked without a manual click.
    useEffect(() => {
        const vin = (formData.vin || '').trim().toUpperCase();
        if (!isOpen || vin.length !== 17 || getVinError(vin) || autoLookupVinRef.current === vin) return;
        autoLookupVinRef.current = vin;
        handleVinLookup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, formData.vin]);

    if (!isOpen) return null;

    const original = buildVehicleForm(initialData, distanceUnit);
    const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));
    const vinValue = formData.vin.trim().toUpperCase();
    const originalVin = original.vin.trim().toUpperCase();

    const duplicateVinError = (vin) => {
        if (!vin) return '';
        const other = vehicles.find((v) => v.vehicle_id !== formData.vehicle_id && String(v.vin || '').toUpperCase() === vin);
        return other ? 'This VIN is already on another vehicle in your garage.' : '';
    };

    const isVinLocked = (key) => vinLockedFields.has(key);

    async function handleVinLookup() {
        setVinNote('');
        setVinReplaced([]);
        const error = (vinValue ? getVinError(vinValue) : 'Enter a VIN first.') || duplicateVinError(vinValue);
        if (error) {
            setSaveError(error);
            return;
        }
        setSaveError(null);
        setIsLookingUpVin(true);
        try {
            const result = await lookupVin(vinValue);
            if (!result.ok) {
                setVinNote(result.unavailable
                    ? "VIN lookup isn't available right now. You can still enter the details manually."
                    : `${result.error} You can still enter the details manually.`);
                return;
            }
            // Locked keys depend only on the decode; apply to the latest form state.
            const { locked, replaced } = applyVinSpecs(formData, result.specs);
            setVinLockedFields(new Set(locked));
            setVinReplaced(replaced);
            setFormData((prev) => applyVinSpecs(prev, result.specs).next);
        } finally {
            setIsLookingUpVin(false);
        }
    }

    const fail = (message) => {
        setSaveError(message);
        setIsSaving(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setSaveError(null);

        let make = formData.make.trim();
        let model = formData.model.trim();
        let year = formData.year ? parseInt(formData.year, 10) : null;
        const maxYear = new Date().getFullYear() + 2;

        if (!make || !model || !year) return fail('Year, make, and model are required.');
        if (year < 1900 || year > maxYear) return fail(`Year must be between 1900 and ${maxYear}. You entered ${formData.year}.`);

        let odometerMiles = null;
        const mileageText = formData.mileage.trim();
        const mileageChanged = mileageText.replace(/,/g, '') !== original.mileage;
        if (mileageText && mileageChanged) {
            if (!/^\d[\d,]*$/.test(mileageText)) return fail('Mileage must be a whole number.');
            odometerMiles = toMiles(mileageText, distanceUnit);
            if (odometerMiles === null || odometerMiles > MAX_ODOMETER_MILES) return fail(ODOMETER_TOO_HIGH_MESSAGE);
        }

        // VIN: optional, but once saved it can be changed, not removed (service history
        // and documents are tied to this vehicle record either way).
        if (!vinValue && originalVin) return fail('A saved VIN can be corrected but not removed.');
        const vinError = getVinError(vinValue) || duplicateVinError(vinValue);
        if (vinError) return fail(vinError);
        const vinChanged = Boolean(vinValue) && vinValue !== originalVin;
        if (vinChanged && originalVin && !confirmVinChange) {
            setConfirmVinChange(true);
            setIsSaving(false);
            return;
        }
        setConfirmVinChange(false);

        try {
            // The VIN is the source of truth for the specs it provides (they are locked in
            // the form). A new/changed VIN the decoder rejects blocks the save; if the
            // service is unavailable, the entered details are saved.
            let vinAgrees = false;
            let source = formData;
            if (vinValue) {
                const lookup = await lookupVin(vinValue);
                if (vinChanged && !lookup.ok && !lookup.unavailable) {
                    return fail(`${lookup.error || "This VIN doesn't appear to be valid."} Please check it.`);
                }
                if (lookup.ok) {
                    source = applyVinSpecs(formData, lookup.specs).next;
                    make = source.make.trim();
                    model = source.model.trim();
                    year = parseInt(source.year, 10) || year;
                    vinAgrees = true;
                }
            }
            const makeModelUnchanged = make === original.make.trim() && model === original.model.trim();

            // ── Client-side static make/model check (instant, no network) ───────
            const MODEL_BELONGS_TO = {};
            if (validationMetadata?.model_belongs_to_make) {
                Object.entries(validationMetadata.model_belongs_to_make).forEach(([k, v]) => {
                    MODEL_BELONGS_TO[k.toLowerCase()] = v;
                });
            }
            const MAKE_ALIASES = {};
            if (validationMetadata?.static_make_aliases) {
                Object.entries(validationMetadata.static_make_aliases).forEach(([k, v]) => {
                    MAKE_ALIASES[k.toLowerCase()] = v.toLowerCase();
                });
            }
            const skipMakeModelCheck = vinAgrees || makeModelUnchanged;
            const correctMake = MODEL_BELONGS_TO[model.toLowerCase().replace(/\s+/g, ' ')];
            const normAlias = (m) => MAKE_ALIASES[m] || m;
            if (!skipMakeModelCheck && correctMake && normAlias(correctMake.toLowerCase()) !== normAlias(make.toLowerCase())) {
                return fail(`'${model}' is a ${correctMake} model, not a ${make} model. Did you mean: ${correctMake} ${model}?`);
            }

            // ── Server-side make/model validation (NHTSA + static) ──────────────
            const userDetailsBaseUrl = getUserDetailsBaseUrl();
            let canonicalMake = make;
            let canonicalModel = model;
            let canonicalYear = year;
            if (!skipMakeModelCheck) {
                try {
                    const validateResp = await fetch(`${userDetailsBaseUrl}/api/validate_vehicle`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ make, model, year }),
                    });
                    const vData = await validateResp.json().catch(() => ({}));
                    if (vData?.valid === false) {
                        const reason = vData.invalid_reason || "That make/model combination doesn't look right.";
                        const suggestions = vData.suggestions || [];
                        return fail(reason + (suggestions.length
                            ? ` Did you mean: ${suggestions.join(', ')}?`
                            : ' Please check the make and model and try again.'));
                    }
                    if (vData?.valid === true) {
                        canonicalMake = vData.canonical_make || make;
                        canonicalModel = vData.canonical_model || model;
                        canonicalYear = vData.canonical_year || year;
                    }
                } catch (validateErr) {
                    console.warn('Server vehicle validation failed, proceeding (client check passed):', validateErr);
                }
            }

            // ── Save ─────────────────────────────────────────────────────────────
            const optional = Object.fromEntries(OPTIONAL_TEXT_FIELDS.map((key) => [key, String(source[key] || '').trim()]));
            const clearFields = formData.vehicle_id
                ? OPTIONAL_TEXT_FIELDS.filter((key) => !optional[key] && original[key].trim())
                : [];
            const response = await fetch(`${userDetailsBaseUrl}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phone_number: phone,
                    vehicle_id: formData.vehicle_id || null,
                    is_new_vehicle: !formData.vehicle_id,
                    make: canonicalMake || null,
                    model: canonicalModel || null,
                    year: canonicalYear ? parseInt(canonicalYear, 10) : null,
                    vin: vinValue || null,
                    ...Object.fromEntries(OPTIONAL_TEXT_FIELDS.map((key) => [key, optional[key] || null])),
                    ...(clearFields.length ? { clear_fields: clearFields } : {}),
                    ...(odometerMiles !== null ? { odometer_reading: odometerMiles, odometer_unit: 'mi' } : {}),
                }),
            });
            if (!response.ok) throw new Error('Failed to save vehicle');
            let payload = null;
            try {
                payload = await response.json();
            } catch (_) {
                payload = null;
            }
            if (payload?.status === 'error' || payload?.error) {
                throw new Error(payload.error || 'Failed to save vehicle');
            }
            const savedVehicle = {
                vehicle_id: payload?.vehicle_id || formData.vehicle_id || null,
                make: canonicalMake,
                model: canonicalModel,
                year: canonicalYear,
                vin: vinValue || null,
                ...optional,
                mileage: odometerMiles ?? parseMileageNumber(getVehicleMileageValue(initialData)),
            };
            await onSave(savedVehicle);
            const wantLog = saveAndLogRef.current;
            saveAndLogRef.current = false;
            onClose();
            if (wantLog && onSaveAndLogService) onSaveAndLogService(savedVehicle);
        } catch (err) {
            console.error('Save Error:', err);
            setSaveError(err.message || 'Failed to save vehicle. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-[32px] shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-8 duration-500">
                <div className="p-6 md:p-8">
                    <header className="flex justify-between items-start mb-6">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-black text-black tracking-tight">
                                {formData.vehicle_id ? 'Edit vehicle' : 'Add a vehicle'}
                            </h2>
                            <p className="text-black/40 font-semibold mt-1">
                                {formData.vehicle_id ? 'Keep your vehicle details up to date.' : 'Add the details you know. You can decode a VIN to fill in specifications.'}
                            </p>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" className="p-2 hover:bg-black/5 rounded-full transition-colors text-black">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
                        </button>
                    </header>

                    {saveError && (
                        <div role="alert" className="mb-5 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-bold flex items-center gap-3">
                            <span>⚠️</span>
                            {saveError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                        <VehicleInput label="VIN">
                            <div className="flex gap-2">
                                <input
                                    value={formData.vin}
                                    onChange={(e) => {
                                        setField('vin', sanitizeVinInput(e.target.value));
                                        setVinNote('');
                                        // Editing the VIN unlocks the decoded fields until the next lookup.
                                        setVinLockedFields(new Set());
                                        setVinReplaced([]);
                                        setConfirmVinChange(false);
                                    }}
                                    placeholder="17-character VIN"
                                    autoCapitalize="characters"
                                    spellCheck={false}
                                    className={`${vehicleInputClass} min-w-0 font-mono tracking-wide`}
                                />
                                <button
                                    type="button"
                                    onClick={handleVinLookup}
                                    disabled={isLookingUpVin || !vinValue}
                                    className="shrink-0 rounded-2xl border border-[#d9d9d9] bg-white px-4 text-xs font-black uppercase tracking-widest text-black hover:bg-[#f7f7f7] disabled:opacity-40"
                                >
                                    {isLookingUpVin ? 'Decoding…' : 'Decode'}
                                </button>
                            </div>
                        </VehicleInput>
                        {vinNote && <p className="m-0 -mt-2 px-1 text-[11px] font-semibold text-[#414141]">{vinNote}</p>}
                        {vinLockedFields.size > 0 && <p className="m-0 -mt-2 px-1 text-[11px] font-semibold text-[#414141]">{VIN_LOCKED_NOTE}</p>}
                        {vinReplaced.length > 0 && <p className="m-0 -mt-2 px-1 text-[11px] font-semibold text-[#8a5a00]">{replacedNote(vinReplaced)}</p>}

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <VehicleInput label="Year" required>
                                <input value={formData.year} disabled={isVinLocked('year')} inputMode="numeric" onChange={(e) => setField('year', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="e.g. 2022" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label="Make" required>
                                <input value={formData.make} disabled={isVinLocked('make')} onChange={(e) => setField('make', e.target.value)} placeholder="e.g. Toyota" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label="Model" required>
                                <input value={formData.model} disabled={isVinLocked('model')} onChange={(e) => setField('model', e.target.value)} placeholder="e.g. Camry" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label="Trim">
                                <input value={formData.trim} disabled={isVinLocked('trim')} onChange={(e) => setField('trim', e.target.value)} placeholder="e.g. SE" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label={`Mileage (${distanceUnit === 'km' ? 'km' : 'miles'})`}>
                                <input value={formData.mileage} inputMode="numeric" onChange={(e) => setField('mileage', e.target.value.replace(/[^\d,]/g, ''))} placeholder="e.g. 45000" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label="Engine">
                                <input value={formData.engine} disabled={isVinLocked('engine')} onChange={(e) => setField('engine', e.target.value)} placeholder="e.g. 2.5L 4-cylinder" className={vehicleInputClass} />
                            </VehicleInput>
                            <VehicleInput label="Transmission">
                                <SpecSelect value={formData.transmission} disabled={isVinLocked('transmission')} options={TRANSMISSION_OPTIONS} onChange={(v) => setField('transmission', v)} />
                            </VehicleInput>
                            <VehicleInput label="Drivetrain">
                                <SpecSelect value={formData.drivetrain} disabled={isVinLocked('drivetrain')} options={DRIVETRAIN_OPTIONS} onChange={(v) => setField('drivetrain', v)} />
                            </VehicleInput>
                            <VehicleInput label="Fuel Type">
                                <SpecSelect value={formData.fuel_type} disabled={isVinLocked('fuel_type')} options={FUEL_TYPE_OPTIONS} onChange={(v) => setField('fuel_type', v)} />
                            </VehicleInput>
                            <VehicleInput label="License Plate">
                                <input value={formData.plate_number} onChange={(e) => setField('plate_number', e.target.value)} placeholder="Plate #" className={vehicleInputClass} />
                            </VehicleInput>
                        </div>

                        {confirmVinChange && (
                            <div role="alertdialog" aria-label="Confirm VIN change" className="rounded-2xl border border-[#f3d9a4] bg-[#fff8ea] p-4 text-xs text-[#4a3a12]">
                                <p className="m-0 font-bold">
                                    Change VIN from <span className="font-mono">{originalVin}</span> to <span className="font-mono">{vinValue}</span>?
                                </p>
                                <p className="m-0 mt-1">Service history, documents and diagnostics stay with this vehicle.</p>
                                <div className="mt-3 flex gap-2">
                                    <button type="submit" className="rounded-full bg-black px-4 py-2 text-[11px] font-black uppercase tracking-widest text-white">Change VIN</button>
                                    <button type="button" onClick={() => { setConfirmVinChange(false); setField('vin', original.vin); }} className="rounded-full border border-black/10 bg-white px-4 py-2 text-[11px] font-black uppercase tracking-widest text-black">Keep current</button>
                                </div>
                            </div>
                        )}

                        {!confirmVinChange && (
                            <div className="mt-2 flex flex-wrap items-center justify-center gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={isSaving}
                                    className="rounded-full border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                                {!formData.vehicle_id && (
                                    <button
                                        type="button"
                                        disabled={isSaving}
                                        onClick={(e) => { saveAndLogRef.current = true; e.currentTarget.form?.requestSubmit(); }}
                                        className="rounded-full border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                                    >
                                        {isSaving && saveAndLogRef.current ? 'Saving…' : 'Save & Log Service'}
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    onClick={() => { saveAndLogRef.current = false; }}
                                    className="rounded-full bg-[#2541b2] px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#1e3596] disabled:cursor-wait disabled:opacity-60"
                                >
                                    {isSaving && !saveAndLogRef.current ? 'Saving…' : (formData.vehicle_id ? 'Save Changes' : 'Save Vehicle')}
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

// --- Command-center helpers ---

const normalizeModDetails = (mod) => {
    const details = mod?.details;
    if (!details) return {};
    if (typeof details === 'string') {
        try {
            const parsed = JSON.parse(details);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : { note: String(parsed) };
        } catch {
            return { note: details };
        }
    }
    return typeof details === 'object' && !Array.isArray(details) ? details : {};
};

const humanizeModValue = (value) => String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatModDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const modStatusLabel = (mod) => {
    const details = normalizeModDetails(mod);
    const raw = mod?.status || details.status || details.stage || details.state || details.phase || details.planning_status;
    const label = humanizeModValue(raw);
    const installedDate = formatModDate(mod?.installed_at);

    if (label) {
        return label.toLowerCase() === 'installed' && installedDate ? `Installed ${installedDate}` : label;
    }
    if (mod?.installed_at) return installedDate ? `Installed ${installedDate}` : 'Installed';
    return 'Planned';
};

const modSummary = (mod) => {
    const details = normalizeModDetails(mod);
    return details.summary || details.notes || details.note || details.description || details.next_step || '';
};

// Derive a status banner (label + description + next step) and whether a report
// exists, from the ticket's lifecycle stage. Presentational defaults only.
const getTicketStatusMeta = (ticket) => {
    const stage = String(ticket?.stage || '').toLowerCase();
    const display = String(ticket?.display_status || '').toLowerCase();
    const hasReport = ['plan', 'service', 'monitoring'].includes(stage) || display.includes('report');
    if (hasReport) {
        return {
            hasReport: true,
            statusLabel: 'Report Ready',
            description: 'The symptom report is ready. Review the findings and start the first check before replacing any parts.',
            nextStep: 'Get a free OBD-II scan at any auto parts store to retrieve any stored fault codes',
        };
    }
    if (['intake', 'safety_assessment', 'diagnostics', ''].includes(stage)) {
        return {
            hasReport: false,
            statusLabel: 'Diagnosing',
            description: 'CarFido is still narrowing down the cause. Continue the chat to add details and get to a report.',
            nextStep: 'Answer Dash’s follow-up questions so it can pinpoint the most likely cause',
        };
    }
    return {
        hasReport: false,
        statusLabel: ticket?.display_status || 'In Progress',
        description: 'This diagnostic is in progress. Pick up where you left off.',
        nextStep: null,
    };
};

// Most-recent activity for a ticket, as an epoch-ms number. Active Work is sorted newest-first
// (the newest item must appear at the TOP, not the bottom), so this is the sort key.
const getTicketRecency = (ticket) => {
    const ts = new Date(
        ticket?.last_activity_at || ticket?.updated_at || ticket?.opened_at || ticket?.created_at || ticket?.started_at || 0
    ).getTime();
    return Number.isFinite(ts) ? ts : 0;
};

// Words that carry no signal when matching a logged repair to the open cases it resolves — generic
// repair verbs, the "only runs on MAX"-style symptom filler, and short connectives.
const SERVICE_MATCH_STOPWORDS = new Set([
    'the', 'and', 'for', 'only', 'with', 'your', 'repair', 'repairs', 'replace', 'replacement',
    'replaced', 'service', 'serviced', 'fixed', 'change', 'changed', 'check', 'checked', 'inspect',
    'inspection', 'install', 'installed', 'work', 'done', 'operates', 'operate', 'operating',
    'runs', 'running', 'speed', 'speeds', 'level', 'levels', 'this', 'that', 'has', 'have',
    'from', 'into', 'issue', 'issues', 'problem', 'problems', 'restored', 'restore', 'again',
]);

// Significant (≥4-char, non-generic) tokens from a title, deduped. "blower", "resistor", "motor",
// "hvac" survive; "only", "runs", "max", "the" drop out.
const serviceMatchTokens = (text = '') => Array.from(new Set(
    String(text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length >= 4 && !SERVICE_MATCH_STOPWORDS.has(t))
));

// Open diagnostic cases a just-logged repair likely resolves: same vehicle, still open, and
// sharing at least one significant keyword with the service title (e.g. "blower"). Conservative on
// purpose — it only ever OFFERS these to the driver to close, never closes anything automatically.
const findOpenCasesResolvedByService = (serviceTitle, tickets, vehicle, vehicleLabel) => {
    const serviceTokens = serviceMatchTokens(serviceTitle);
    if (serviceTokens.length === 0) return [];
    const serviceSet = new Set(serviceTokens);
    return (tickets || []).filter((ticket) => {
        if (isTicketClosed(ticket)) return false;
        if (!ticketRealId(ticket)) return false; // only real tickets can be PATCH-closed
        if (vehicle && !ticketBelongsToVehicle(ticket, vehicle, vehicleLabel)) return false;
        const ticketTokens = serviceMatchTokens(
            ticket?._displayTitle || ticket?.title || ticket?.issue || ticket?.display_status || ''
        );
        return ticketTokens.some((t) => serviceSet.has(t));
    });
};

const SectionHeading = ({ title, subtitle, right }) => (
    <div className="mb-4 flex items-end justify-between gap-4">
        <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-black tracking-tight text-black"><span className="h-4 w-1 shrink-0 rounded-full bg-[#D81F26]" aria-hidden="true" />{title}</h2>
            {subtitle && <p className="mt-1 text-sm font-semibold text-[#6B6B6B]">{subtitle}</p>}
        </div>
        {right && <div data-testid="section-heading-right" className="shrink-0 whitespace-nowrap">{right}</div>}
    </div>
);

// "8 of 10 shown" — what the counter means, spelled out for screen readers.
const ShownCount = ({ shown, total, noun }) => (
    <span className="text-sm font-bold text-[#6B6B6B]">
        <span aria-hidden="true">{shown} of {total} shown</span>
        <span className="sr-only">Showing {shown} of {total} {noun}</span>
    </span>
);

const getGarageRequestTitle = (request) => (
    request?.services?.[0]?.title || request?.shop_ready_summary?.customerComplaint || 'Garage repair request'
);

const isActiveGarageRequestStatus = (status) => !['declined', 'failed', 'cancelled', 'picked_up', 'completed'].includes(String(status || '').toLowerCase());

const getGarageRequestStatusClass = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (['ready_for_pickup', 'picked_up', 'scheduled', 'estimate_accepted'].includes(normalized)) {
        return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    }
    if (['estimate_sent', 'in_service'].includes(normalized)) {
        return 'border-[#dfd0ff] bg-[#f7f2ff] text-[#6f3cc3]';
    }
    if (['declined', 'failed'].includes(normalized)) {
        return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    }
    return 'border-[#d9d9d9] bg-white text-[#414141]';
};

const findStoredReportForTicket = (reports, ticket) => {
    const ticketId = String(ticketRealId(ticket) || '');
    const threadId = String(ticketThreadId(ticket) || '');
    return (reports || []).find((report) => {
        const reportTicketId = String(report?.diagnosticTicketId || report?.ticketId || '');
        const reportThreadId = String(report?.threadId || report?.thread_id || '');
        return (ticketId && reportTicketId && reportTicketId === ticketId) ||
            (threadId && reportThreadId && reportThreadId === threadId);
    }) || null;
};

const getGarageDiagnosticTicketId = (ticket) => {
    const candidate = ticket?.diagnostic_ticket_id || ticket?.ticket_id || ticket?.id || '';
    return /^\d+$/.test(String(candidate)) ? candidate : '';
};

const ActiveWorkCard = ({ ticket, isRecommended, onContinue, onViewReport, onRequestEstimate, onMarkResolved, onAskDash, onDismiss, isResolving, isViewingReport }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const meta = getTicketStatusMeta(ticket);
    const pathLabel = ticketPathLabel(ticket.path);
    const timeAgo = formatTimeAgo(ticket.last_activity_at || ticket.opened_at);

    return (
        <div className={`group relative overflow-hidden rounded-2xl border bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(0,0,0,0.09)] ${isRecommended ? 'border-[#F5C2C4]' : 'border-[#e5e5e5] hover:border-[#d9d9d9]'}`}>
            {isRecommended && <span className="absolute inset-y-0 left-0 w-1 bg-[#D81F26]" aria-hidden="true" />}
            <div className="px-4 pt-3.5 pb-3.5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.14em] ${isRecommended ? 'text-[#D81F26]' : 'text-[#6B6B6B]'}`}>
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${isRecommended ? 'bg-[#D81F26] motion-safe:animate-pulse' : 'bg-[#8C8C8C]'}`} />
                            {isRecommended ? 'CarFido Recommends' : meta.statusLabel}
                        </p>
                        <h3 className="mt-1.5 line-clamp-2 break-words text-base font-black leading-tight text-black" title={ticketTitle(ticket)}>{ticketTitle(ticket)}</h3>
                        <p className="mt-0.5 text-sm font-semibold text-[#414141]">
                            {ticketVehicleLabel(ticket)}{pathLabel ? ` · ${pathLabel}` : ''}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        {timeAgo && <span className="text-xs font-bold text-[#414141]">{timeAgo}</span>}
                        <button
                            type="button"
                            onClick={() => setIsExpanded((value) => !value)}
                            aria-expanded={isExpanded}
                            className="rounded-full p-1.5 text-[#414141] transition-colors hover:bg-[#F2F2F2] hover:text-[#2A2A2A]"
                            title={isExpanded ? 'Hide details' : 'Show details'}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            >
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2.5">
                    <button
                        type="button"
                        onClick={() => onContinue(ticket)}
                        className={`group/continue inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-black text-white transition-all active:scale-[0.98] ${isRecommended ? 'bg-[#D81F26] shadow-[0_6px_16px_rgba(216,31,38,0.22)] hover:bg-[#B8181E]' : 'bg-[#414141] hover:bg-[#2A2A2A]'}`}
                    >
                        Continue
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover/continue:translate-x-0.5"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsExpanded((value) => !value)}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white px-4 py-2.5 text-sm font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#F9F9F9]"
                    >
                        {isExpanded ? 'Hide details' : 'Details'}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="border-t border-[#ededed] bg-[#F9F9F9] px-5 py-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#414141]">{meta.statusLabel}</p>
                    <p className="mt-1.5 text-sm font-semibold leading-relaxed text-[#535353]">{meta.description}</p>
                    {meta.nextStep && (
                        <p className="mt-2 text-sm font-bold text-[#414141]">Next: {meta.nextStep}</p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2 pt-1">
                        <button
                            type="button"
                            onClick={() => onViewReport(ticket)}
                            disabled={isViewingReport}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-black text-black transition-all hover:border-[#414141] disabled:opacity-60"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" /></svg>
                            {isViewingReport ? 'Opening…' : 'View Report'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onRequestEstimate(ticket)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-black text-[#414141] transition-all hover:border-[#D81F26] hover:text-[#D81F26]"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11h6" /><path d="M9 15h4" /><rect x="5" y="3" width="14" height="18" rx="2" /></svg>
                            Get Estimate
                        </button>
                        <button
                            type="button"
                            onClick={() => onAskDash(ticket)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#d9d9d9] bg-white px-3 py-2 text-xs font-black text-black transition-all hover:border-[#414141]"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>
                            Ask CarFido
                        </button>
                        <button
                            type="button"
                            onClick={() => onMarkResolved(ticket)}
                            disabled={isResolving}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-black text-emerald-600 transition-all hover:bg-emerald-50 disabled:opacity-50"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>
                            {isResolving ? 'Resolving…' : 'Mark Resolved'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onDismiss(ticket)}
                            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-black text-rose-500 transition-all hover:bg-rose-50"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const MyVehiclesSection = ({
    vehicles,
    activeVehicleId,
    onSwitch,
    switchingId,
    onOpenVehicle,
    documentCounts = {},
    onAddOdometer,
    distanceUnit = 'mi',
}) => {
    if (!vehicles || vehicles.length === 0) return null;
    return (
        <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading
                title="Vehicles"
                subtitle="The selected vehicle is used for chat and reports."
                right={<span className="text-sm font-bold text-[#6B6B6B]">{vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}</span>}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {vehicles.map((v, index) => {
                    const isActive = String(v.vehicle_id) === String(activeVehicleId);
                    const isSwitching = String(switchingId) === String(v.vehicle_id);
                    const label = getVehicleLabel(v) || 'Vehicle';
                    const mileage = formatVehicleMileage(getVehicleMileageValue(v), distanceUnit);
                    const docsCount = documentCounts[String(v.vehicle_id)] || 0;
                    const supportingLabel = [v?.trim, v?.plate_number ? `Plate ${v.plate_number}` : ''].filter(Boolean).join(' · ');
                    return (
                        <div
                            key={v.vehicle_id}
                            aria-current={isActive ? 'true' : undefined}
                            style={{ animationDelay: `${Math.min(index * 70, 350)}ms` }}
                            className={`animate-dash-intro-rise flex flex-col gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all sm:flex-row sm:items-center ${
                                isActive
                                    ? 'border-[#D81F26]/45 bg-white shadow-[0_6px_20px_rgba(216,31,38,0.08)] ring-1 ring-[#D81F26]/10'
                                    : 'border-[#d9d9d9] bg-white hover:border-[#414141] hover:bg-[#F9F9F9]'
                            }`}
                        >
                            <div className="flex w-full min-w-0 items-center gap-3">
                                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isActive ? 'bg-[#FDEDEE] text-[#D81F26]' : 'bg-[#f5f5f5] text-[#414141]'}`}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center">
                                        <span className="truncate text-[15px] font-black text-black">{label}</span>
                                    </div>
                                    <div className="mt-0.5 truncate text-[13px] font-semibold text-[#414141]">
                                        {/* With the Use button: 'Tap Use to make active' */}
                                        {isSwitching ? 'Switching…' : supportingLabel || (isActive ? 'Currently used by CarFido' : 'Switch from the vehicle slider or chat')}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {mileage ? (
                                            <span className="rounded-full border border-[#d9d9d9] bg-white px-2 py-0.5 text-[11px] font-black text-[#414141]">
                                                <CountUpMileage text={mileage} />
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => onAddOdometer?.(v)}
                                                className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-black text-amber-700 transition hover:bg-amber-100"
                                            >
                                                Add mileage
                                            </button>
                                        )}
                                        <span className="rounded-full border border-[#d9d9d9] bg-white px-2 py-0.5 text-[11px] font-black text-[#414141]">
                                            {docsCount} doc{docsCount === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex w-full shrink-0 gap-1.5 sm:w-auto sm:flex-col">
                                {/* Use button hidden: the active vehicle is switched from the vehicle slider or chat.
                                    Uncomment to bring it back.
                                {!isActive && (
                                    <button
                                        type="button"
                                        onClick={() => { if (!isSwitching) onSwitch(v); }}
                                        disabled={isSwitching}
                                        className="flex-1 rounded-lg bg-[#414141] px-3 py-1.5 text-xs font-black text-white transition hover:bg-[#2A2A2A] disabled:cursor-wait disabled:opacity-60 sm:flex-none"
                                    >
                                        Use
                                    </button>
                                )}
                                */}
                                <button
                                    type="button"
                                    onClick={() => onOpenVehicle?.(v)}
                                    className="flex-1 rounded-lg border border-[#d9d9d9] bg-white px-3 py-1.5 text-xs font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F9F9F9] sm:flex-none"
                                >
                                    Details
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

const ActiveWorkSection = ({ tickets, onContinue, onViewReport, onRequestEstimate, onMarkResolved, onAskDash, onDismiss, resolvingId, viewingReportId, onStartDiagnostic }) => {
    const [showAll, setShowAll] = useState(false);
    const visibleTickets = showAll ? tickets : tickets.slice(0, 2);
    // "CarFido Recommends" is a concrete next action, not just "the top card". Mark AT MOST ONE
    // ticket — the first one that actually has a ready report to act on — so a still-"Diagnosing"
    // card is never mislabelled as a recommendation. If nothing is report-ready, no card is
    // recommended (every card shows its own real status). (tickets are already newest-first.)
    const recommendedTicket = tickets.find((ticket) => getTicketStatusMeta(ticket).hasReport) || null;
    const recommendedTicketKey = recommendedTicket
        ? String(ticketRealId(recommendedTicket) || ticketThreadId(recommendedTicket))
        : null;

    return (
        <section className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading
                title="Active Work"
                subtitle="Open diagnostics and repair follow-ups."
                right={tickets.length > 0 && <ShownCount shown={visibleTickets.length} total={tickets.length} noun="active work items" />}
            />
            {tickets.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-6 py-8 text-center">
                    <p className="text-sm font-bold text-[#414141]">No open diagnostics for this vehicle.</p>
                    <button
                        type="button"
                        onClick={onStartDiagnostic}
                        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#D81F26] px-5 py-2.5 text-sm font-black text-white transition-all hover:bg-[#B8181E]"
                    >
                        Start a Diagnostic
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {visibleTickets.map((ticket, index) => (
                        // Wrapper carries the entrance so the card keeps its own hover lift.
                        <div
                            key={ticketRealId(ticket) || ticketThreadId(ticket)}
                            className="animate-dash-intro-rise"
                            style={{ animationDelay: `${Math.min(150 + index * 80, 550)}ms` }}
                        >
                        <ActiveWorkCard
                            ticket={ticket}
                            isRecommended={recommendedTicketKey != null && String(ticketRealId(ticket) || ticketThreadId(ticket)) === recommendedTicketKey}
                            onContinue={onContinue}
                            onViewReport={onViewReport}
                            onRequestEstimate={onRequestEstimate}
                            onMarkResolved={onMarkResolved}
                            onAskDash={onAskDash}
                            onDismiss={onDismiss}
                            isResolving={String(resolvingId) === String(ticketRealId(ticket))}
                            isViewingReport={viewingReportId != null && String(viewingReportId) === String(ticketRealId(ticket) || ticketThreadId(ticket))}
                        />
                        </div>
                    ))}
                    {tickets.length > 2 && (
                        <button
                            type="button"
                            onClick={() => setShowAll((value) => !value)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white py-3 text-sm font-black text-[#414141] transition-colors hover:bg-[#F9F9F9]"
                        >
                            {showAll ? 'Show less' : `Show all ${tickets.length}`}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={showAll ? 'rotate-180' : ''}><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

const GarageChatsSection = ({ requests, onOpen }) => {
    const [showClosed, setShowClosed] = useState(false);
    const activeRequests = requests.filter((request) => isActiveGarageRequestStatus(request.request_status));
    const closedRequests = requests.filter((request) => !isActiveGarageRequestStatus(request.request_status));
    const visibleRequests = showClosed ? requests : activeRequests;

    return (
        <section className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading
                title="Garage Chats"
                subtitle="Active shop conversations and repair updates."
                right={requests.length > 0 && <span className="text-sm font-bold text-[#414141]">{activeRequests.length} active</span>}
            />
            {visibleRequests.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-6 py-7 text-center">
                    <p className="text-sm font-bold text-[#414141]">
                        {requests.length ? 'No active Garage chats for this vehicle.' : 'No Garage chats for this vehicle yet.'}
                    </p>
                    {closedRequests.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowClosed(true)}
                            className="mt-3 rounded-xl border border-[#d9d9d9] bg-white px-4 py-2 text-xs font-black text-[#414141]"
                        >
                            Show completed chats
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {visibleRequests.map((request) => {
                        const appointment = formatPartnerScheduledAppointment(request);
                        const estimate = request.latest_estimate;
                        const messageCount = Array.isArray(request.messages) ? request.messages.length : 0;
                        const detailParts = [
                            request.partner_shop?.name || 'Partner shop',
                            estimate ? formatPartnerMoney(estimate.total_amount, estimate.currency) : '',
                            appointment,
                            messageCount > 0 ? `${messageCount} msg` : '',
                        ].filter(Boolean);
                        return (
                            <button
                                key={request.request_id}
                                type="button"
                                onClick={() => onOpen(request)}
                                className="w-full rounded-2xl border border-[#e5e5e5] bg-white px-4 py-3.5 text-left shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition hover:border-teal-300 hover:bg-teal-50/20"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-teal-200 bg-teal-50 text-teal-600">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 9 5 3h14l2 6" />
                                                <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                                                <path d="M9 22V12h6v10" />
                                            </svg>
                                        </div>
                                        <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em] ${getGarageRequestStatusClass(request.request_status)}`}>
                                                {getPartnerStatusLabel(request.request_status)}
                                            </span>
                                        </div>
                                        <p className="mt-2 line-clamp-2 break-words text-base font-black text-black" title={getGarageRequestTitle(request)}>{getGarageRequestTitle(request)}</p>
                                        {detailParts.length > 0 && (
                                            <p className="mt-0.5 truncate text-sm font-semibold text-[#414141]">
                                                {detailParts.join(' · ')}
                                            </p>
                                        )}
                                    </div>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-black text-teal-700">Open in CarFido</span>
                                </div>
                            </button>
                        );
                    })}
                    {closedRequests.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowClosed((value) => !value)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white py-3 text-sm font-black text-[#414141] transition-colors hover:bg-[#F9F9F9]"
                        >
                            {showClosed ? 'Hide completed chats' : `Show ${closedRequests.length} completed chat${closedRequests.length === 1 ? '' : 's'}`}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

const MaintenanceRow = ({ item, onConfirm, onDismiss, onAskDash }) => {
    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const alreadyAnswered = item.userResponse === 'not_done';
    const showConfirmPrompt = Boolean(item.needsConfirmation && onConfirm);
    const handleConfirm = async (done) => {
        if (confirming) return;
        setConfirming(true);
        try {
            await onConfirm(item.key, done);
        } finally {
            setConfirming(false);
        }
    };
    const handleDismiss = async (e) => {
        e.stopPropagation();
        if (dismissing || !onDismiss) return;
        setDismissing(true);
        try {
            await onDismiss(item.key);
        } finally {
            setDismissing(false);
        }
    };
    const badge = item.status === 'overdue'
        ? 'border-red-200 bg-red-50 text-red-600'
        : item.status === 'due-soon'
        ? 'border-amber-200 bg-amber-50 text-amber-600'
        : 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]';
    const badgeLabel = item.status === 'overdue'
        ? 'Overdue'
        : item.status === 'due-soon'
        ? 'Due soon'
        : 'Upcoming';
    const milesRemaining = item.milesRemaining;
    const hasMiles = item.type === 'mileage' && milesRemaining !== null && milesRemaining !== undefined;
    return (
        <div className="border-b border-[#f2f2f2] last:border-b-0">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#F9F9F9]"
            >
                <div className="min-w-0">
                    <p className="line-clamp-2 break-words text-base font-black leading-snug text-black" title={item.title}>{item.title}</p>
                    <p className="mt-0.5 text-sm font-semibold text-[#414141]">{item.dueLabel || 'Not scheduled yet'}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2.5">
                    {alreadyAnswered && (
                        <span className="rounded-full border border-red-200 bg-white px-2.5 py-1 text-[11px] font-black text-red-500">Not done yet</span>
                    )}
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black ${badge}`}>{badgeLabel}</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6" /></svg>
                </div>
            </button>
            {open && (
                <div className="px-5 pb-4 -mt-1">
                    <div className="rounded-xl bg-[#F9F9F9] border border-[#efefef] px-4 py-3">
                        <p className="text-sm font-semibold text-[#535353]">{item.description}</p>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs font-bold text-[#414141]">
                            <span>{item.intervalLabel}</span>
                            {hasMiles && milesRemaining < 0 && item.hasHistory && (
                                <span className="text-red-600">{Number(Math.abs(milesRemaining)).toLocaleString('en-US')} miles overdue</span>
                            )}
                            {hasMiles && milesRemaining >= 0 && (
                                <span>~{Number(milesRemaining).toLocaleString('en-US')} miles to go</span>
                            )}
                            <span>Next: {item.dueLabel || 'Not scheduled yet'}</span>
                        </div>
                        {item.source === 'no_history' && (
                            <p className="mt-2 text-[11px] font-bold text-[#414141]">Estimated. Add this service to your history for an accurate reminder.</p>
                        )}
                        {(onAskDash || onDismiss) && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#efefef] pt-3">
                                {onAskDash && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onAskDash(item); }}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[#dedede] bg-white px-3 py-1.5 text-[12px] font-black text-[#414141] transition hover:bg-[#F9F9F9]"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>
                                        Ask CarFido
                                    </button>
                                )}
                                {onDismiss && (
                                    <button
                                        type="button"
                                        onClick={handleDismiss}
                                        disabled={dismissing}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[#e7e7e7] bg-white px-3 py-1.5 text-[12px] font-black text-[#737373] transition hover:bg-[#f4f4f4] disabled:opacity-60"
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                        {dismissing ? 'Dismissing…' : 'Dismiss'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showConfirmPrompt && (() => {
                const isOverdue = item.status === 'overdue';
                const tone = isOverdue
                    ? { wrap: 'border-[#f3d6d6] bg-[#fef6f6]', text: 'text-[#a13b3b]', no: 'border-[#d8c2c2] text-[#8a5a5a] hover:bg-[#fbeaea]' }
                    : { wrap: 'border-amber-200 bg-amber-50', text: 'text-amber-700', no: 'border-amber-200 text-amber-700 hover:bg-amber-100' };
                const service = item.title.toLowerCase();
                const message = alreadyAnswered
                    ? `Had your ${service} done since?`
                    : `Already had your ${service} done?`;
                return (
                    <div className={`border-t px-5 py-3 ${tone.wrap}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2.5">
                            <p className={`text-sm font-black ${tone.text}`}>{message}</p>
                            <div className="flex shrink-0 items-center gap-2">
                                <button
                                    type="button"
                                    disabled={confirming}
                                    onClick={() => handleConfirm(true)}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[12px] font-black text-white transition hover:bg-emerald-700 disabled:opacity-60"
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                    {confirming ? 'Saving…' : 'Completed'}
                                </button>
                                <button
                                    type="button"
                                    disabled={confirming}
                                    onClick={() => handleConfirm(false)}
                                    className={`rounded-full border bg-white px-3.5 py-1.5 text-[12px] font-black transition disabled:opacity-60 ${tone.no}`}
                                >
                                    Not completed
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

const MaintenanceReminderBanner = ({ overdue, dueSoon }) => {
    if (!overdue && !dueSoon) return null;
    const parts = [];
    if (overdue) parts.push(`${overdue} ${overdue === 1 ? 'service' : 'services'}`);
    if (dueSoon) parts.push(`${dueSoon} due soon`);
    const isUrgent = overdue > 0;
    const tone = isUrgent
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';
    return (
        <div className={`mb-4 flex items-center gap-3 rounded-2xl border px-4 py-3 ${tone}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
            </svg>
            <p className="text-sm font-black">
                {isUrgent ? 'Maintenance overdue: ' : 'Maintenance reminder: '}
                <span className="font-bold">{parts.join(' · ')}.</span>
            </p>
        </div>
    );
};

const UpcomingMaintenanceSection = ({ items, onConfirm, onDismiss, onAskDash, unavailable = false }) => {
    const [showAll, setShowAll] = useState(false);
    // Always keep overdue/due-soon items in the default (collapsed) view so users
    // are alerted without having to expand — the whole point of the reminder.
    const urgentCount = items.filter((it) => it.status === 'overdue' || it.status === 'due-soon').length;
    const defaultCount = Math.max(3, urgentCount);
    const visible = showAll ? items : items.slice(0, defaultCount);
    const overdue = items.filter((it) => it.status === 'overdue').length;
    const dueSoon = items.filter((it) => it.status === 'due-soon').length;
    return (
        <section className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading
                title="Upcoming Maintenance"
                right={items.length > 0 && <ShownCount shown={visible.length} total={items.length} noun="maintenance items" />}
            />
            {unavailable && (
                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-500">
                    Showing estimated intervals. Live tracking couldn't load. Refresh the page to try again.
                </div>
            )}
            <MaintenanceReminderBanner overdue={overdue} dueSoon={dueSoon} />
            <div className="overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                {visible.map((item) => (
                    <MaintenanceRow key={item.key} item={item} onConfirm={onConfirm} onDismiss={onDismiss} onAskDash={onAskDash} />
                ))}
                {items.length > defaultCount && (
                    <button
                        type="button"
                        onClick={() => setShowAll((v) => !v)}
                        className="flex w-full items-center justify-center gap-1.5 border-t border-[#f2f2f2] py-3 text-sm font-black text-[#414141] transition-colors hover:bg-[#F9F9F9]"
                    >
                        {showAll ? 'Show less' : `Show all ${items.length}`}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={showAll ? 'rotate-180' : ''}><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                )}
            </div>
        </section>
    );
};

// Lets the user record a service they already had done OUTSIDE the app, so their
// maintenance history and upcoming reminders reflect that work.
const LogPastServiceSection = ({ distanceUnit = 'mi', onLog }) => {
    const [open, setOpen] = useState(false);
    const [serviceKey, setServiceKey] = useState(MAINTENANCE_SCHEDULE[0]?.key || '');
    const [odometer, setOdometer] = useState('');
    const [dateStr, setDateStr] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    // Set when the service mileage exceeds the vehicle's current odometer — the driver must
    // confirm updating the odometer before the service is saved (CHAT-MAINT-006).
    const [confirmMsg, setConfirmMsg] = useState('');
    const unitLabel = distanceUnit === 'km' ? 'km' : 'mi';
    const todayISO = new Date().toLocaleDateString('en-CA');

    const canSave = Boolean(serviceKey) && dateStr.trim() !== '' && !saving;

    // Shared save routine. `confirmOdometer` is true only after the driver chooses
    // "Update and log service" for a mileage above the current odometer.
    const doLog = async (confirmOdometer) => {
        setSaving(true);
        setSavedMsg('');
        setErrorMsg('');
        try {
            const res = await onLog(serviceKey, odometer, dateStr, confirmOdometer);
            if (res?.confirmRequired) {
                setConfirmMsg(res.message);
                return;
            }
            const ok = res === true || res?.ok === true;
            if (ok) {
                const svc = MAINTENANCE_SCHEDULE.find((s) => s.key === serviceKey);
                setSavedMsg(`Logged ${svc ? svc.title : 'service'} to your history.`);
                setConfirmMsg('');
                setOdometer('');
                setDateStr('');
            } else {
                setErrorMsg(res?.message || 'Could not save. Please try again.');
                setConfirmMsg('');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (!canSave || !onLog) return;
        if (!dateStr) {
            setSavedMsg('');
            setErrorMsg('Service date is required.');
            return;
        }
        // A service record logs work already done — block a future date up front.
        if (dateStr && dateStr > todayISO) {
            setSavedMsg('');
            setErrorMsg('Service history records work already done. Pick today or an earlier date.');
            return;
        }
        // Enforce the 1,000,000-mile maximum (#412) on the canonical (miles) value.
        const odoMiles = odometer.trim() ? toMiles(odometer, distanceUnit) : null;
        if (odoMiles !== null && odoMiles > MAX_ODOMETER_MILES) {
            setSavedMsg('');
            setErrorMsg(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        await doLog(false);
    };

    return (
        <section className="mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-[#F9F9F9]"
                >
                    <div className="min-w-0">
                        <p className="text-base font-black text-black">Had a service done elsewhere?</p>
                        <p className="mt-0.5 text-sm font-semibold text-[#414141]">Log it here to keep your reminders accurate.</p>
                    </div>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6" /></svg>
                </button>
                {open && (
                    <div className="border-t border-[#f2f2f2] px-5 py-4 space-y-3">
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">Service</span>
                            <select
                                value={serviceKey}
                                onChange={(e) => setServiceKey(e.target.value)}
                                className="mt-1.5 w-full rounded-xl border border-[#e1e1e1] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                            >
                                {MAINTENANCE_SCHEDULE.map((s) => (
                                    <option key={s.key} value={s.key}>{s.title}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">Odometer at service <span className="font-semibold normal-case tracking-normal text-[#414141]">(optional)</span></span>
                                <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-2">
                                    <input
                                        value={odometer}
                                        onChange={(e) => setOdometer(e.target.value)}
                                        placeholder={unitLabel === 'km' ? '95,000' : '60,000'}
                                        inputMode="numeric"
                                        className="min-w-0 rounded-xl border border-[#e1e1e1] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                                    />
                                    <span className="flex items-center px-1 text-xs font-black text-[#414141]">{unitLabel}</span>
                                </div>
                            </div>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-[0.12em] text-[#414141]">Date <span className="text-[#c0392b]">*</span></span>
                                <USDateInput
                                    value={dateStr}
                                    max={todayISO}
                                    onChange={(iso) => { setDateStr(iso); setErrorMsg(''); }}
                                    className="mt-1.5 w-full rounded-xl border border-[#e1e1e1] bg-white px-3.5 py-2.5 pr-10 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                                />
                            </div>
                        </div>
                        {confirmMsg ? (
                            // Service mileage is above the current odometer — confirm updating it.
                            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                                <p className="text-sm font-bold text-amber-900 leading-snug">{confirmMsg}</p>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => doLog(true)}
                                        disabled={saving}
                                        className="inline-flex items-center gap-2 rounded-xl bg-[#D81F26] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:bg-[#b8b8b8]"
                                    >
                                        {saving ? 'Saving…' : 'Update and log service'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setConfirmMsg(''); setErrorMsg(''); }}
                                        disabled={saving}
                                        className="inline-flex items-center rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm font-black text-black/60 transition hover:bg-black/5 disabled:opacity-50"
                                    >
                                        Go back
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={!canSave}
                                    className="inline-flex items-center gap-2 rounded-xl bg-[#D81F26] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:bg-[#b8b8b8]"
                                >
                                    {saving ? 'Saving…' : 'Log this service'}
                                </button>
                                {savedMsg && <span className="text-xs font-bold text-emerald-600">{savedMsg}</span>}
                                {errorMsg && <span className="text-xs font-bold text-rose-600">{errorMsg}</span>}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

const PlannedModsSection = ({ mods, vehicleLabel, onViewMod }) => {
    const [showAll, setShowAll] = useState(false);
    const visible = showAll ? mods : mods.slice(0, 3);

    return (
        <section className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading
                title="Planned Mods"
                right={mods.length > 0 && <ShownCount shown={visible.length} total={mods.length} noun="planned mods" />}
            />
            <div className="overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                {mods.length === 0 ? (
                    <div className="px-5 py-6 text-center">
                        <p className="text-sm font-bold text-[#414141]">No planned mods logged for this vehicle.</p>
                    </div>
                ) : (
                    <>
                        {visible.map((mod, index) => {
                            const category = humanizeModValue(mod?.category);
                            const status = modStatusLabel(mod);
                            const summary = modSummary(mod);
                            const meta = [vehicleLabel, category, status].filter(Boolean).join(' · ');

                            return (
                                <div key={mod?.mod_id || `${mod?.name || 'mod'}-${index}`} className="border-b border-[#f2f2f2] last:border-b-0">
                                    <div className="flex items-center justify-between gap-3 px-5 py-4">
                                        <div className="min-w-0">
                                            <p className="line-clamp-2 break-words text-base font-black text-black">{mod?.name || 'Planned modification'}</p>
                                            {meta && <p className="mt-0.5 text-sm font-semibold text-[#414141]">{meta}</p>}
                                            {summary && <p className="mt-1 truncate text-xs font-semibold text-[#414141]">{summary}</p>}
                                        </div>
                                        {onViewMod && (
                                            <button
                                                type="button"
                                                onClick={() => onViewMod(mod)}
                                                className="shrink-0 rounded-lg px-3 py-2 text-sm font-black text-black transition-colors hover:bg-[#F9F9F9]"
                                            >
                                                View
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {mods.length > 3 && (
                            <button
                                type="button"
                                onClick={() => setShowAll((v) => !v)}
                                className="flex w-full items-center justify-center gap-1.5 border-t border-[#f2f2f2] py-3 text-sm font-black text-[#414141] transition-colors hover:bg-[#F9F9F9]"
                            >
                                {showAll ? 'Show less' : `View all ${mods.length} mods`}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={showAll ? 'rotate-180' : ''}><path d="m6 9 6 6 6-6" /></svg>
                            </button>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

const ToolInventorySection = ({ tools, onAdd, onRemove, isLoading }) => {
    const [name, setName] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [busy, setBusy] = useState(false);
    const [listOpen, setListOpen] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        const trimmed = name.trim();
        if (!trimmed) return;
        setBusy(true);
        try {
            await onAdd(trimmed);
            setName('');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SectionHeading title="Tool Inventory" />
            <div className="overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">

                {/* ── Summary row (always visible) ── */}
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#f4f4f4] text-[#414141]">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" />
                            </svg>
                        </div>
                        <div>
                            {isLoading ? (
                                <p className="text-sm font-semibold text-[#414141]">Loading tools…</p>
                            ) : (
                                <p className="text-base font-black text-black">
                                    {tools.length === 0 ? 'No tools added yet' : `${tools.length} tool${tools.length === 1 ? '' : 's'} owned`}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* View Tools toggle — only shown when tools exist */}
                        {tools.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setListOpen((v) => !v)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white px-4 py-2 text-sm font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#F9F9F9]"
                            >
                                {listOpen ? 'Hide Tools' : 'View Tools'}
                                <svg
                                    width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.6"
                                    strokeLinecap="round" strokeLinejoin="round"
                                    className={`transition-transform duration-200 ${listOpen ? 'rotate-180' : ''}`}
                                >
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </button>
                        )}
                        {/* Add button always available */}
                        <button
                            type="button"
                            onClick={() => { setShowAdd((v) => !v); if (!listOpen) setListOpen(true); }}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-[#414141] px-4 py-2 text-sm font-black text-white transition-all hover:bg-[#2A2A2A]"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12h14M12 5v14" /></svg>
                            Add
                        </button>
                    </div>
                </div>

                {/* ── Expanded dropdown list ── */}
                {(listOpen || showAdd) && (
                    <div className="border-t border-[#e5e5e5]">

                        {/* Tool rows */}
                        {tools.length > 0 && (
                            <ul className="max-h-64 divide-y divide-[#f2f2f2] overflow-y-auto">
                                {tools.map((tool) => (
                                    <li key={tool.tool_id} className="flex items-center justify-between gap-3 px-5 py-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#f5f5f5] text-[#414141]">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.4-.6-.6-2.4z" />
                                                </svg>
                                            </div>
                                            <span className="truncate text-sm font-semibold text-[#414141]">{tool.name}</span>
                                            {tool.category && (
                                                <span className="hidden shrink-0 rounded-full bg-[#f2f2f2] px-2 py-0.5 text-[11px] font-bold text-[#414141] sm:inline">
                                                    {tool.category}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onRemove(tool)}
                                            className="shrink-0 rounded-lg p-1.5 text-black/25 transition-colors hover:bg-rose-50 hover:text-rose-500"
                                            title="Remove tool"
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                                                <path d="M18 6 6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Add form */}
                        {showAdd && (
                            <div className="border-t border-[#f2f2f2] px-5 py-4">
                                <form onSubmit={submit} className="flex gap-2">
                                    <input
                                        autoFocus
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Socket wrench set"
                                        className="min-w-0 flex-1 rounded-xl border border-[#e1e1e1] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                                    />
                                    <button
                                        type="submit"
                                        disabled={busy || !name.trim()}
                                        className="rounded-xl bg-[#D81F26] px-5 py-2.5 text-sm font-black text-white transition-all hover:bg-[#B8181E] disabled:opacity-50"
                                    >
                                        {busy ? 'Adding…' : 'Add'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

// --- Main Page ---

const GaragePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { authState, openAuthModal, recentDiagnostics, onSelectThread, refreshDiagnosticTickets, threads, onStartNewChat, askDash } = useOutletContext();
    const [garageData, setGarageData] = useState(null);
    const [storedDiagnosticReports, setStoredDiagnosticReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // UI State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [selectedCar, setSelectedCar] = useState(null);
    const [carToRemove, setCarToRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [removeError, setRemoveError] = useState(null);
    const [switchingVehicleId, setSwitchingVehicleId] = useState(null);

    // Command-center state
    const [tools, setTools] = useState([]);
    const [toolsLoading, setToolsLoading] = useState(false);
    const [documentCounts, setDocumentCounts] = useState({ all: 0, byVehicle: {} });
    const [garageShopRequests, setGarageShopRequests] = useState([]);
    const [maintenanceSchedule, setMaintenanceSchedule] = useState(null);
    const [maintenanceUnavailable, setMaintenanceUnavailable] = useState(false);
    // Per-vehicle maintenance schedules (vehicle_id -> { items, upcomingCount, overdueCount,
    // unavailable }) so every vehicle card can show its own "N upcoming · N overdue".
    const [maintenanceByVehicle, setMaintenanceByVehicle] = useState({});
    // Session-only vehicle photo previews (vehicle_id -> object URL). There's no backend
    // vehicle-photo field yet, so an added photo shows on the card for this session and is
    // also saved to that vehicle's Glove Box.
    const [vehiclePhotos, setVehiclePhotos] = useState({});
    const [attentionDismissed, setAttentionDismissed] = useState(false);
    const [garageUploadFeedback, setGarageUploadFeedback] = useState(null);
    // "Update odometer" modal (per-vehicle, direct edit + save).
    const [odometerModalVehicle, setOdometerModalVehicle] = useState(null);
    const [odometerModalValue, setOdometerModalValue] = useState('');
    const [odometerModalError, setOdometerModalError] = useState('');
    const [odometerModalSaving, setOdometerModalSaving] = useState(false);
    // "Log a Service" modal (free-form service record saved to Service History).
    const [logServiceOpen, setLogServiceOpen] = useState(false);
    const [logServiceForm, setLogServiceForm] = useState(null); // { vehicleId, title, date, mileage, performedBy, cost, shopName, notes, serviceKey }
    // Two-step flow: 'edit' (enter details) → 'review' (confirm). The record is only created
    // on Confirm & Save from the review step — never on entering review.
    const [logServiceStep, setLogServiceStep] = useState('edit');
    const [logServiceError, setLogServiceError] = useState('');
    const [logServiceWarning, setLogServiceWarning] = useState('');
    const [logServiceSaving, setLogServiceSaving] = useState(false);
    // After a service is logged, offer to close the open diagnostic cases it resolves.
    // { serviceTitle, tickets: [...] } while the offer is shown; null otherwise.
    const [closeCasesOffer, setCloseCasesOffer] = useState(null);
    const [closingMatchedCases, setClosingMatchedCases] = useState(false);
    // "Add Document" 4-step wizard.
    const [addDocOpen, setAddDocOpen] = useState(false);
    const [addDocVehicleId, setAddDocVehicleId] = useState(null);
    // Right-side vehicle detail drawer (opened by clicking a card's name).
    const [detailVehicleId, setDetailVehicleId] = useState(null);
    // Hidden file input shared by the card "Add document" / "Add photo" actions.
    const garageFileInputRef = useRef(null);
    const garageUploadTargetRef = useRef(null); // { vehicleId, kind: 'document'|'photo' }
    const [shopDrawerTicket, setShopDrawerTicket] = useState(null);
    // Maintenance "Find a Shop" flow: holds the selected items + the lightweight estimate
    // ticket created for them. When set, the shop drawer opens in maintenance mode.
    // Shape: { vehicle, vehicleLabel, items:[{key,title,dueLabel,status}], diagnosticTicketId, creating }
    const [maintShopDrawer, setMaintShopDrawer] = useState(null);
    // Tracks the last shop-open navigation intent we've already acted on, so closing the drawer
    // (which re-runs the open effect) can't re-open it, while a genuinely NEW "Find a Shop" still does.
    const consumedShopIntentRef = useRef('');
    const [dismissedTicketIds, setDismissedTicketIds] = useState(() => new Set());
    const [resolvingTicketId, setResolvingTicketId] = useState(null);
    // "Mark Resolved" confirmation: before closing a ticket, ask the driver whether the
    // expected service was performed and capture any details to save to service history.
    const [resolveTicket, setResolveTicket] = useState(null);
    const [resolveServicePerformed, setResolveServicePerformed] = useState(true);
    const [resolveNotes, setResolveNotes] = useState('');
    const [resolveOdometer, setResolveOdometer] = useState('');
    const [resolveError, setResolveError] = useState('');
    // Which ticket's "View Report" is currently fetching from the backend (for button feedback
    // and to prevent double-clicks while a report is being fetched/generated).
    const [viewingReportTicketId, setViewingReportTicketId] = useState(null);

    const isAuthenticated = authState?.status === 'authenticated';
    // Preferred distance unit for display/conversion (odometer is stored in miles).
    // Declared before the handlers below so their useCallback deps can reference it.
    // #409: CarFido communicates only in imperial — always display/enter miles, ignoring
    // any stored metric preference.
    const distanceUnit = 'mi';
    const dismissedStorageKey = useMemo(() => getDismissedGarageWorkStorageKey(authState?.identity), [authState?.identity]);

    useEffect(() => {
        setDismissedTicketIds(readLocalStringSet(dismissedStorageKey));
    }, [dismissedStorageKey]);

    const fetchGarageData = useCallback(async () => {
        if (!authState.identity) return;
        setIsLoading(true);
        setError(null);
        try {
            // Dynamically get the User Details service URL (defaults to port 8001 in local dev)
            const userDetailsBaseUrl = getUserDetailsBaseUrl();
            const identity = authState.identity;
            // Now fetching both vehicles and workplans
            const response = await fetch(`${userDetailsBaseUrl}/api/lookup_user_data?phone_number=${encodeURIComponent(identity)}&query=vehicle_worklist`);
            
            if (!response.ok) throw new Error('Failed to fetch garage data');
            
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            
            setGarageData(data.result);
            setStoredDiagnosticReports(loadStoredDiagnosticReports());
        } catch (err) {
            console.error('Garage Fetch Error:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [authState.identity]);

    // Authoritative upcoming-maintenance schedule (computed server-side from the
    // vehicle's odometer + onboarding baselines + real service history). Falls back
    // to the client-side interval estimate if the backend is unavailable.
    const fetchMaintenanceSchedule = useCallback(async (vehicleId) => {
        if (!authState.identity) return;
        try {
            const url = `${getUserDetailsBaseUrl()}/api/maintenance-schedule/${encodeURIComponent(authState.identity)}`
                + (vehicleId ? `?vehicle_id=${encodeURIComponent(vehicleId)}` : '');
            const resp = await fetch(url);
            const data = await resp.json();
            if (data && Array.isArray(data.items) && data.items.length > 0) {
                setMaintenanceSchedule(data);
                setMaintenanceUnavailable(false);
            } else {
                setMaintenanceSchedule(null);
                setMaintenanceUnavailable(true);
            }
        } catch (err) {
            console.warn('Garage maintenance-schedule fetch skipped:', err);
            setMaintenanceSchedule(null);
            setMaintenanceUnavailable(true);
        }
    }, [authState.identity]);

    // Fetch the maintenance schedule for EVERY vehicle so each card shows its own
    // "N upcoming · N overdue". One call per vehicle (there's no bulk endpoint); results
    // are merged into maintenanceByVehicle keyed by vehicle_id.
    const fetchMaintenanceForVehicles = useCallback(async (vehicleList) => {
        if (!authState.identity || !Array.isArray(vehicleList) || vehicleList.length === 0) return;
        const results = await Promise.all(vehicleList.map(async (v) => {
            const vid = v?.vehicle_id;
            if (!vid) return null;
            try {
                const url = `${getUserDetailsBaseUrl()}/api/maintenance-schedule/${encodeURIComponent(authState.identity)}?vehicle_id=${encodeURIComponent(vid)}`;
                const resp = await fetch(url);
                const data = await resp.json().catch(() => ({}));
                const items = Array.isArray(data?.items) ? data.items : [];
                if (items.length === 0) return [vid, { items: [], upcomingCount: 0, overdueCount: 0, unavailable: true }];
                const overdueCount = items.filter((it) => it.status === 'overdue').length;
                const upcomingCount = items.length;
                return [vid, { items, upcomingCount, overdueCount, unavailable: false }];
            } catch (_) {
                return [vid, { items: [], upcomingCount: 0, overdueCount: 0, unavailable: true }];
            }
        }));
        setMaintenanceByVehicle((prev) => {
            const next = { ...prev };
            for (const entry of results) {
                if (entry) next[entry[0]] = entry[1];
            }
            return next;
        });
    }, [authState.identity]);

    // "Why recommended?" — AI-generated, vehicle-specific explanation for a single
    // maintenance item. Returns the explanation object (or null on failure); the
    // card caches it per item so the panel is generated at most once per open.
    const fetchMaintenanceWhy = useCallback(async (vehicleId, serviceKey) => {
        if (!authState.identity || !vehicleId || !serviceKey) return null;
        try {
            const url = `${getUserDetailsBaseUrl()}/api/maintenance-why/${encodeURIComponent(authState.identity)}`
                + `?vehicle_id=${encodeURIComponent(vehicleId)}&service_key=${encodeURIComponent(serviceKey)}`;
            const resp = await fetch(url);
            const data = await resp.json().catch(() => ({}));
            if (data?.status === 'ok') return data;
            return null;
        } catch (_) {
            return null;
        }
    }, [authState.identity]);

    // Record the user's answer to "did you complete this overdue maintenance?" and
    // refresh the schedule (Yes advances the next-due; Not yet keeps it overdue).
    const handleMaintenanceConfirm = useCallback(async (serviceKey, done, vehicleIdArg) => {
        if (!authState.identity) return;
        // Per-vehicle cards pass the card's vehicle_id; fall back to the active vehicle.
        const vehicleId = vehicleIdArg ?? garageData?.active_vehicle?.vehicle_id;
        const targetVehicle = (garageData?.vehicles || []).find((v) => String(v?.vehicle_id) === String(vehicleId))
            || garageData?.active_vehicle;
        const body = {
            phone_number: authState.identity,
            vehicle_id: vehicleId,
            service_key: serviceKey,
            done: Boolean(done),
        };
        if (done) {
            // Marking a maintenance item "Completed" must ALSO log it to service history and
            // advance the schedule. The backend requires a service date for done=true, so
            // default to today, and pass the vehicle's current odometer (when known) so the
            // record carries a mileage and the next-due is computed from it. The odometer never
            // exceeds the current reading here, so it won't trigger the odometer-update prompt.
            body.performed_at = new Date().toISOString().slice(0, 10);
            const curOdo = getVehicleMileageValue(targetVehicle);
            const curOdoNum = curOdo != null ? Number(String(curOdo).replace(/,/g, '')) : NaN;
            if (Number.isFinite(curOdoNum) && curOdoNum >= 0) body.odometer = Math.round(curOdoNum);
        }
        try {
            await fetch(`${getUserDetailsBaseUrl()}/api/maintenance-confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            });
        } catch (err) {
            console.warn('Maintenance confirm failed:', err);
        } finally {
            if (String(vehicleId) === String(garageData?.active_vehicle?.vehicle_id)) {
                await fetchMaintenanceSchedule(vehicleId);
            }
            // Refresh this vehicle's per-card schedule too.
            if (targetVehicle) await fetchMaintenanceForVehicles([targetVehicle]);
            // The completed service now lives in Service History — let the Glove Box /
            // history views refresh so it shows up immediately.
            try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
        }
    }, [authState.identity, garageData?.active_vehicle, garageData?.vehicles, fetchMaintenanceSchedule, fetchMaintenanceForVehicles]);

    // Dismiss a maintenance item the user doesn't want to see, then refresh.
    const handleMaintenanceDismiss = useCallback(async (serviceKey) => {
        if (!authState.identity) return;
        const vehicleId = garageData?.active_vehicle?.vehicle_id;
        try {
            await fetch(`${getUserDetailsBaseUrl()}/api/maintenance-dismiss`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone_number: authState.identity,
                    vehicle_id: vehicleId,
                    service_key: serviceKey,
                    dismissed: true,
                }),
            });
        } catch (err) {
            console.warn('Maintenance dismiss failed:', err);
        } finally {
            await fetchMaintenanceSchedule(vehicleId);
        }
    }, [authState.identity, garageData?.active_vehicle?.vehicle_id, fetchMaintenanceSchedule]);

    // Log a service the user completed OUTSIDE the app: records it to service history
    // and advances the schedule from that odometer/date. Returns true on success.
    // Ask CarFido about a specific maintenance item — opens chat with a ready question.
    const handleMaintenanceAskDash = useCallback((item) => {
        if (!item) return;
        const vehicleLabel = getVehicleLabel(garageData?.active_vehicle) || 'my vehicle';
        const dueBit = item.dueLabel ? ` (scheduled around ${item.dueLabel})` : '';
        // Lead phrase "About upcoming maintenance" is recognized server-side as an
        // informational maintenance question so it is NOT treated as a fault to
        // diagnose (no diagnostic ticket / report is created).
        const question = `About upcoming maintenance: what is the ${item.title} service on my ${vehicleLabel}${dueBit}, why is this maintenance needed, when is it typically due, and roughly what does it cost? This is a general maintenance info question, not a problem to diagnose.`;
        if (typeof askDash === 'function') {
            // Always open a NEW chat for a maintenance question so it never lands in the
            // conversation the user currently has open.
            askDash(question, { newChat: true });
        } else if (onStartNewChat) {
            onStartNewChat();
            navigate('/chat');
        }
    }, [garageData?.active_vehicle, askDash, onStartNewChat, navigate]);

    const fetchTools = useCallback(async () => {
        if (!authState.identity) return;
        setToolsLoading(true);
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/tools/${encodeURIComponent(authState.identity)}`);
            const data = await resp.json();
            setTools(Array.isArray(data?.tools) ? data.tools : []);
        } catch (err) {
            console.warn('Garage tools fetch skipped:', err);
            setTools([]);
        } finally {
            setToolsLoading(false);
        }
    }, [authState.identity]);

    const fetchDocumentCounts = useCallback(async () => {
        if (!authState.identity) return;
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me`, {
                credentials: 'include',
            });
            const data = await resp.json().catch(() => ({}));
            const docs = Array.isArray(data?.documents) ? data.documents : [];
            const byVehicle = {};
            docs.forEach((doc) => {
                const vehicleId = getDocumentVehicleId(doc);
                if (!vehicleId) return;
                const key = String(vehicleId);
                byVehicle[key] = (byVehicle[key] || 0) + 1;
            });
            setDocumentCounts({ all: docs.length, byVehicle });
        } catch (err) {
            console.warn('Garage document counts skipped:', err);
            setDocumentCounts({ all: 0, byVehicle: {} });
        }
    }, [authState.identity]);

    useEffect(() => {
        if (isAuthenticated && authState.identity) {
            fetchGarageData();
            fetchTools();
            fetchDocumentCounts();
            // Pull the latest diagnostic-ticket statuses so Active Work matches the sidebar /
            // chat header. Without this, a case closed from the chat (e.g. finishing the DIY
            // guide) stays in the shared context as "open" and lingers in Active Work even though
            // the sidebar already shows it Resolved — the status-out-of-sync bug.
            refreshDiagnosticTickets?.();
            const refreshGarage = () => {
                fetchGarageData();
                fetchDocumentCounts();
                refreshDiagnosticTickets?.();
            };
            window.addEventListener('dash_vehicle_updated', refreshGarage);
            window.addEventListener('dash_vehicle_switched', refreshGarage);
            return () => {
                window.removeEventListener('dash_vehicle_updated', refreshGarage);
                window.removeEventListener('dash_vehicle_switched', refreshGarage);
            };
        }
        return () => {
            window.removeEventListener('dash_vehicle_updated', fetchGarageData);
            window.removeEventListener('dash_vehicle_switched', fetchGarageData);
        };
    }, [isAuthenticated, authState.identity, fetchGarageData, fetchTools, fetchDocumentCounts, refreshDiagnosticTickets]);

    // Refresh the maintenance schedule whenever the active vehicle changes (also
    // reacts to vehicle updates that fire the refresh events above via garageData).
    const activeVehicleIdForSchedule = garageData?.active_vehicle?.vehicle_id;
    useEffect(() => {
        if (isAuthenticated && authState.identity && activeVehicleIdForSchedule) {
            fetchMaintenanceSchedule(activeVehicleIdForSchedule);
        } else {
            setMaintenanceSchedule(null);
        }
    }, [isAuthenticated, authState.identity, activeVehicleIdForSchedule, fetchMaintenanceSchedule]);

    // Load every vehicle's maintenance schedule (for the per-vehicle cards) whenever the
    // vehicle set changes. Keyed on the sorted vehicle-id list so it re-runs on add/remove.
    const vehicleIdsKey = (garageData?.vehicles || []).map((v) => v?.vehicle_id).filter(Boolean).sort().join(',');
    useEffect(() => {
        if (isAuthenticated && authState.identity) {
            fetchMaintenanceForVehicles(garageData?.vehicles || []);
        } else {
            setMaintenanceByVehicle({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, authState.identity, vehicleIdsKey, fetchMaintenanceForVehicles]);

    useEffect(() => {
        if (!isAuthenticated || !authState.identity) {
            setGarageShopRequests([]);
            return undefined;
        }
        let cancelled = false;
        let intervalId = null;
        const loadRequests = () => {
            listPartnerRepairRequests()
                .then((requests) => {
                    if (!cancelled) setGarageShopRequests(requests);
                })
                .catch(() => {
                    if (!cancelled) setGarageShopRequests([]);
                });
        };
        loadRequests();
        intervalId = window.setInterval(loadRequests, 6000);
        return () => {
            cancelled = true;
            if (intervalId) window.clearInterval(intervalId);
        };
    }, [isAuthenticated, authState.identity]);

    const handleAddTool = useCallback(async (name) => {
        if (!authState.identity) return;
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: authState.identity, name }),
            });
            const data = await resp.json();
            if (data?.status === 'success' && data.tool) {
                setTools((prev) => [...prev, data.tool]);
            }
        } catch (err) {
            console.error('Add tool failed:', err);
        }
    }, [authState.identity]);

    const handleRemoveTool = useCallback(async (tool) => {
        if (!authState.identity || !tool?.tool_id) return;
        // Optimistic removal.
        setTools((prev) => prev.filter((t) => t.tool_id !== tool.tool_id));
        try {
            await fetch(`${getUserDetailsBaseUrl()}/api/tools/${tool.tool_id}?phone_number=${encodeURIComponent(authState.identity)}`, {
                method: 'DELETE',
            });
        } catch (err) {
            console.error('Remove tool failed:', err);
            fetchTools();
        }
    }, [authState.identity, fetchTools]);

    // Adding a vehicle uses the guided chat "Add your vehicle" form (with VIN
    // lookup + odometer), not the basic modal. Start a fresh chat and signal it
    // to open the vehicle form.
    const handleAddVehicle = () => {
        // Open the Add Vehicle form modal directly (VIN decode + specs), matching the reference.
        setEditingVehicle(null);
        setIsModalOpen(true);
    };
    // "Save & Log Service": after the new vehicle saves, open the Log a Service modal for it.
    const handleSaveAndLogService = (savedVehicle) => {
        const vid = savedVehicle?.vehicle_id;
        const vehicle = (garageData?.vehicles || []).find((v) => String(v?.vehicle_id) === String(vid)) || savedVehicle;
        if (vehicle?.vehicle_id) {
            // Defer so the garage refresh (from onSave) settles first.
            window.setTimeout(() => openLogServiceModal(vehicle), 150);
        }
    };

    const handleEditVehicle = (vehicle) => {
        setEditingVehicle(vehicle);
        setIsModalOpen(true);
    };

    // Make the tapped vehicle the active one. Uses the same endpoint the vehicle
    // selector uses, then refreshes the garage and notifies the selector/chat.
    const handleSwitchVehicle = useCallback(async (vehicle) => {
        if (!authState.identity || !vehicle?.vehicle_id) return;
        setSwitchingVehicleId(vehicle.vehicle_id);
        setError(null);
        try {
            const userDetailsBaseUrl = getUserDetailsBaseUrl();
            const response = await fetch(`${userDetailsBaseUrl}/api/vehicle_selection`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: authState.identity, vehicle_id: vehicle.vehicle_id }),
            });
            if (!response.ok) throw new Error('Failed to switch active vehicle');
            await fetchGarageData();
            // Mirror the vehicle selector: notify listeners (selector, chat) to refetch.
            window.dispatchEvent(new CustomEvent('dash_vehicle_switched'));
        } catch (err) {
            console.error('Switch vehicle error:', err);
            setError(err.message || 'Failed to switch active vehicle');
        } finally {
            setSwitchingVehicleId(null);
        }
    }, [authState.identity, fetchGarageData]);

    useEffect(() => {
        if (!location.state?.openAddVehicle) return;
        setEditingVehicle(null);
        setIsModalOpen(true);
    }, [location.state]);

    const handleVehicleSaved = useCallback(async (savedVehicle) => {
        const addedNewVehicle = !editingVehicle?.vehicle_id;
        await fetchGarageData();

        const detail = buildVehicleAddedSummary(savedVehicle);
        window.dispatchEvent(new CustomEvent('dash_vehicle_updated', { detail }));
        window.dispatchEvent(new CustomEvent('dash_vehicle_switched', { detail }));
        if (addedNewVehicle) markPwaEngagement('vehicle-added');

        if (location.state?.returnToChat) {
            navigate('/chat', {
                replace: true,
                state: {
                    vehicleAdded: detail,
                },
            });
        }
    }, [editingVehicle?.vehicle_id, fetchGarageData, location.state, navigate]);

    const openRemoveConfirm = useCallback((car) => {
        setRemoveError(null);
        setCarToRemove(car);
    }, []);

    const handleRemoveVehicle = useCallback(async () => {
        if (!carToRemove || !authState.identity) return;
        setIsRemoving(true);
        setRemoveError(null);
        try {
            const userDetailsBaseUrl = getUserDetailsBaseUrl();
            const response = await fetch(`${userDetailsBaseUrl}/api/delete_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: authState.identity, vehicle_id: carToRemove.vehicle_id }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || data.status !== 'success') {
                throw new Error(data.error || 'Failed to remove vehicle');
            }
            // Close the detail panel if it was showing the removed car, dismiss the dialog.
            setSelectedCar((prev) => (prev && prev.vehicle_id === carToRemove.vehicle_id ? null : prev));
            setCarToRemove(null);
            // Refresh the garage from the DB and tell the vehicle selector + chat to refetch.
            await fetchGarageData();
            window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
            window.dispatchEvent(new CustomEvent('dash_vehicle_switched'));
        } catch (err) {
            console.error('Remove vehicle error:', err);
            setRemoveError(err.message || 'Failed to remove vehicle');
        } finally {
            setIsRemoving(false);
        }
    }, [carToRemove, authState.identity, fetchGarageData]);

    useEffect(() => {
        const targetTicketId = location.state?.openEstimateTicketId;
        const targetThreadId = location.state?.openEstimateThreadId;
        const shopReport = location.state?.openShopReport;
        if (!targetTicketId && !targetThreadId && !shopReport) return;
        // Act on each open-shop navigation intent exactly once. This lets a fresh "Find a Shop"
        // (new ticket) REPLACE a stale open drawer, while a re-run triggered by CLOSING the drawer
        // (or any re-render before location.state clears) is ignored — so close actually closes.
        const intentKey = JSON.stringify({ targetTicketId, targetThreadId, shopReport });
        if (consumedShopIntentRef.current === intentKey) return;
        consumedShopIntentRef.current = intentKey;

        const target = (recentDiagnostics || []).find((ticket) => (
            (targetTicketId && String(ticketRealId(ticket) || '') === String(targetTicketId)) ||
            (targetThreadId && String(ticketThreadId(ticket) || '') === String(targetThreadId))
        ));
        if (target) {
            setStoredDiagnosticReports(loadStoredDiagnosticReports());
            setShopDrawerTicket(target);
            navigate('/garage', { replace: true, state: null });
            return;
        }
        // No matching diagnostic ticket in the list — e.g. a scheduled-maintenance report. Open
        // the shop drawer from the report's vehicle + issue. If a lightweight maintenance ticket
        // was just created (shopReport.ticketId), the synthetic ticket carries its numeric id so
        // estimate requests are enabled; otherwise it stays contact-only.
        if (shopReport) {
            setStoredDiagnosticReports(loadStoredDiagnosticReports());
            setShopDrawerTicket({
                diagnostic_ticket_id: shopReport.ticketId || undefined,
                vehicle_label: shopReport.vehicle || '',
                title: shopReport.issue || 'Maintenance service',
                symptom_summary: shopReport.issue || '',
                default_zip: shopReport.defaultZip || shopReport.default_zip || '',
            });
            navigate('/garage', { replace: true, state: null });
        }
    }, [location.state, navigate, recentDiagnostics]);

    if (authState?.status === 'loading') {
        return (
            <div className="h-full w-full p-8 flex flex-col items-center justify-center bg-white">
                <div className="w-12 h-12 border-4 border-black/5 border-t-black rounded-full animate-spin"></div>
                <p className="mt-4 text-xs font-black uppercase tracking-widest text-black/30">Authenticating...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center bg-[#F9F9F9] overflow-y-auto">
                <div className="max-w-md w-full animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <div className="w-24 h-24 bg-[#eaeaea] rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black/20">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                    </div>
                    <h2 className="text-3xl font-black text-black tracking-tight mb-4">Your Virtual Garage</h2>
                    <p className="text-black/50 font-semibold leading-relaxed mb-10">
                        Sign in with your phone number to see your saved vehicles, maintenance history, and personalized diagnostic insights.
                    </p>
                    <button 
                        onClick={openAuthModal}
                        className="w-full py-4 bg-black text-white rounded-full font-black uppercase tracking-[0.2em] text-xs shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading && !garageData) {
        return (
            <div className="h-full w-full p-8 flex flex-col items-center justify-center bg-white">
                <div className="w-12 h-12 border-4 border-black/5 border-t-black rounded-full animate-spin"></div>
                <p className="mt-4 text-xs font-black uppercase tracking-widest text-black/30">Opening Garage...</p>
            </div>
        );
    }

    const vehicles = garageData?.vehicles || [];
    const workplans = garageData?.workplans || [];
    const defaultVehicle = vehicles[0] || null;
    // Non-closed diagnostic tickets for the active vehicle drive the Active Work cards.
    const activeVehicleTickets = (recentDiagnostics || []).filter(ticket => !isTicketClosed(ticket));

    // --- Command center: single selected (active) vehicle ---
    // (distanceUnit is declared near the top so the handlers above can use it.)
    const activeVehicle = garageData?.active_vehicle || defaultVehicle;
    const activeVehicleLabel = getVehicleLabel(activeVehicle);
    const profileZip = String(garageData?.region || authState?.profileRegion || '').match(/\b\d{5}\b/)?.[0] || '';
    const profileShopSearchRadiusMiles = Number(garageData?.shop_search_radius_miles) > 0
        ? Number(garageData.shop_search_radius_miles)
        : 50;
    const activeOdometer = getVehicleMileageValue(activeVehicle);
    const activeOdometerNumber = parseMileageNumber(activeOdometer);
    const hasOdometer = activeOdometerNumber !== null && activeOdometerNumber > 0;
    // Map thread_id -> title so Active Work cards can show the real issue name
    // (matching the sidebar) instead of a generic "Start Diagnostic" placeholder.
    const threadTitleById = new Map((threads || []).map((t) => [String(t.thread_id), t.title]));
    // Open diagnostics for the active vehicle, minus any the user dismissed.
    const activeWorkTickets = activeVehicleTickets
        .filter((ticket) => (
            (!activeVehicle || ticketBelongsToVehicle(ticket, activeVehicle, activeVehicleLabel)) &&
            !dismissedTicketIds.has(String(ticketRealId(ticket) || ticketThreadId(ticket)))
        ))
        .map((ticket) => ({ ...ticket, _displayTitle: resolveTicketDisplayTitle(ticket, threadTitleById) }))
        .sort((a, b) => getTicketRecency(b) - getTicketRecency(a));
    // Prefer the authoritative server-computed schedule (uses real service history +
    // onboarding baselines, and can flag "overdue"); fall back to the client-side
    // interval estimate if the backend schedule isn't available yet.
    const maintenanceItems = (maintenanceSchedule?.items && maintenanceSchedule.items.length > 0)
        ? maintenanceSchedule.items
        : buildUpcomingMaintenance(hasOdometer ? activeOdometerNumber : null);
    // Per-vehicle data for the redesigned Garage cards: each vehicle's own open diagnostics
    // and its own maintenance summary (upcoming / overdue).
    const openTicketsByVehicle = (vehicle) => activeVehicleTickets
        .filter((ticket) => (
            ticketBelongsToVehicle(ticket, vehicle, getVehicleLabel(vehicle)) &&
            !dismissedTicketIds.has(String(ticketRealId(ticket) || ticketThreadId(ticket)))
        ))
        .map((ticket) => ({ ...ticket, _displayTitle: resolveTicketDisplayTitle(ticket, threadTitleById) }))
        .sort((a, b) => getTicketRecency(b) - getTicketRecency(a));

    const vehicleCards = vehicles.map((vehicle) => {
        const openTickets = openTicketsByVehicle(vehicle);
        const maint = maintenanceByVehicle[vehicle?.vehicle_id] || { items: [], upcomingCount: 0, overdueCount: 0, unavailable: true };
        return { vehicle, openTickets, maint };
    });
    // Attention banner: vehicles that have open diagnostics.
    const attentionVehicles = vehicleCards.filter((c) => c.openTickets.length > 0);
    const attentionCount = attentionVehicles.reduce((sum, c) => sum + c.openTickets.length, 0);

    // Resolved (closed) diagnostic cases — offered in the Add Document wizard's "Link to
    // Resolved Case" step.
    const resolvedCases = (recentDiagnostics || [])
        .filter((ticket) => isTicketClosed(ticket))
        .map((ticket) => {
            const resolvedAt = ticket?.resolved_at || ticket?.closed_at || ticket?.updated_at || '';
            let resolvedLabel = '';
            if (resolvedAt) {
                const d = new Date(resolvedAt);
                if (!Number.isNaN(d.getTime())) resolvedLabel = `Resolved ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
            }
            return {
                // Only a REAL diagnostic_ticket_id can be linked (the column has an FK); cases
                // without one are omitted from the link list.
                id: ticketRealId(ticket),
                vehicleId: ticket?.vehicle_id,
                title: resolveTicketDisplayTitle(ticket, threadTitleById),
                resolvedLabel,
            };
        })
        .filter((c) => c.id);

    // ── Vehicle detail drawer data (for the vehicle whose name was clicked) ──────────
    const detailVehicle = vehicles.find((v) => String(v?.vehicle_id) === String(detailVehicleId)) || null;
    const detailVehicleLabelStr = detailVehicle ? getVehicleLabel(detailVehicle) : '';
    const detailAllTickets = detailVehicle
        ? (recentDiagnostics || []).filter((t) => ticketBelongsToVehicle(t, detailVehicle, detailVehicleLabelStr))
        : [];
    const _ageLabel = (iso) => {
        if (!iso) return '';
        const days = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
        return Number.isFinite(days) ? `${days}d ago` : '';
    };
    const detailDiagnostics = detailAllTickets
        .map((t) => {
            const closed = isTicketClosed(t);
            return {
                id: `t-${ticketRealId(t) || ticketThreadId(t)}`,
                title: resolveTicketDisplayTitle(t, threadTitleById),
                ageLabel: _ageLabel(t?.created_at || t?.started_at || t?.updated_at),
                statusLabel: closed ? 'Resolved' : 'Open',
                statusTone: closed ? 'resolved' : 'recommended',
                action: closed ? 'report' : 'continue',
                raw: t,
            };
        })
        .sort((a, b) => Number(a.action === 'report') - Number(b.action === 'report'));
    const detailMaintItems = (maintenanceByVehicle[detailVehicleId]?.items || []).map((it) => ({
        key: it.key, title: it.title, dueLabel: it.dueLabel || it.intervalLabel || '',
    }));
    const detailChats = detailVehicle ? [
        ...detailAllTickets.map((t) => {
            const closed = isTicketClosed(t);
            return {
                id: `tc-${ticketRealId(t) || ticketThreadId(t)}`,
                title: resolveTicketDisplayTitle(t, threadTitleById),
                snippet: t?.symptom_summary || t?.issue || t?.summary || '',
                statusLabel: closed ? 'Resolved' : 'Open',
                statusTone: closed ? 'resolved' : 'recommended',
                raw: { _kind: 'ticket', ticket: t },
            };
        }),
        ...garageShopRequests
            .filter((r) => !r?.source_vehicle_id || String(r.source_vehicle_id) === String(detailVehicleId))
            .map((r) => ({
                id: `sc-${r.id || r.request_id || r.diagnostic_ticket_id}`,
                title: r.shop_name || r.partner_shop?.name || 'Repair shop',
                snippet: r.latest_message || r.last_message_preview || '',
                statusLabel: String(r.request_status || '').replace(/_/g, ' ') || '',
                statusTone: 'progress',
                timeLabel: '',
                raw: { _kind: 'shop', request: r },
            })),
    ] : [];
    const handleOpenDetailChat = (raw) => {
        if (raw?._kind === 'shop') handleOpenGarageChat(raw.request);
        else if (raw?._kind === 'ticket') handleOpenTicketThread(raw.ticket);
    };
    const handleOpenDetailDocument = (doc) => {
        navigate('/glovebox', { state: { tab: 'documents', openDocId: doc?.doc_id } });
    };

    const handleStartDiagnostic = () => {
        if (onStartNewChat) onStartNewChat();
        navigate('/chat', { state: { q: 'Start diagnostic' } });
    };

    const handleAddOdometer = async (vehicle = activeVehicle) => {
        // The odometer update flows through chat, which targets the ACTIVE vehicle. If the user
        // tapped "Add Odometer" on a non-active car, make that car active first — otherwise the
        // reading would land on the wrong (currently-active) vehicle.
        if (vehicle?.vehicle_id && String(vehicle.vehicle_id) !== String(activeVehicle?.vehicle_id)) {
            try {
                await handleSwitchVehicle(vehicle);
            } catch (err) {
                console.warn('Add Odometer: failed to switch active vehicle first:', err);
            }
        }
        if (onStartNewChat) onStartNewChat();
        const vehicleLabel = getVehicleLabel(vehicle) || activeVehicleLabel;
        const vehicleContext = vehicleLabel ? ` for ${vehicleLabel}` : '';
        // Pass the target vehicle id explicitly so the odometer update lands on THIS vehicle,
        // even if the active-vehicle context in chat hasn't refetched after the switch yet.
        navigate('/chat', {
            state: {
                q: `Help me add or update the odometer reading${vehicleContext}.`,
                odometerVehicleId: vehicle?.vehicle_id || undefined,
            },
        });
    };

    // "Update odometer" modal: open prefilled with the vehicle's current reading, save the new
    // value straight to THIS vehicle (no chat round-trip), then refresh the garage + schedules.
    const openOdometerModal = (vehicle) => {
        if (!vehicle?.vehicle_id) return;
        const current = getVehicleMileageValue(vehicle);
        const currentNum = current != null ? Number(String(current).replace(/,/g, '')) : null;
        setOdometerModalVehicle(vehicle);
        setOdometerModalValue(Number.isFinite(currentNum) && currentNum > 0 ? String(Math.round(currentNum)) : '');
        setOdometerModalError('');
    };
    const closeOdometerModal = () => {
        if (odometerModalSaving) return;
        setOdometerModalVehicle(null);
        setOdometerModalValue('');
        setOdometerModalError('');
    };
    const handleSaveOdometer = async () => {
        const vehicle = odometerModalVehicle;
        if (!vehicle?.vehicle_id || !authState.identity) return;
        const raw = String(odometerModalValue || '').replace(/,/g, '').trim();
        if (!/^\d+$/.test(raw)) {
            setOdometerModalError('Enter a whole number of miles.');
            return;
        }
        const odoNum = Number(raw);
        if (odoNum > MAX_ODOMETER_MILES) {
            setOdometerModalError(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        setOdometerModalSaving(true);
        setOdometerModalError('');
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone_number: authState.identity,
                    vehicle_id: vehicle.vehicle_id,
                    is_new_vehicle: false,
                    odometer_reading: odoNum,
                    odometer_unit: 'mi',
                    mileage: odoNum,
                }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || data?.status === 'error' || data?.error) {
                throw new Error(data?.error || data?.message || 'Could not update the odometer. Please try again.');
            }
            setOdometerModalVehicle(null);
            setOdometerModalValue('');
            await fetchGarageData();
            await fetchMaintenanceForVehicles([vehicle]);
            if (String(vehicle.vehicle_id) === String(activeVehicle?.vehicle_id)) {
                await fetchMaintenanceSchedule(vehicle.vehicle_id);
            }
            try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
        } catch (err) {
            setOdometerModalError(err?.message || 'Could not update the odometer. Please try again.');
        } finally {
            setOdometerModalSaving(false);
        }
    };

    // Per-card "Log service": open the Log a Service modal, prefilled for THIS vehicle (date =
    // today, mileage = its current reading). Saved directly to Service History on submit.
    const openLogServiceModal = (vehicle) => {
        if (!vehicle?.vehicle_id) return;
        const current = getVehicleMileageValue(vehicle);
        const currentNum = current != null ? Number(String(current).replace(/,/g, '')) : null;
        setLogServiceForm({
            vehicleId: vehicle.vehicle_id,
            title: '',
            date: new Date().toISOString().slice(0, 10),
            mileage: Number.isFinite(currentNum) && currentNum > 0 ? String(Math.round(currentNum)) : '',
            performedBy: 'shop',
            cost: '',
            shopName: '',
            notes: '',
            serviceKey: null,
        });
        setLogServiceStep('edit');
        setLogServiceError('');
        setLogServiceWarning('');
        setLogServiceOpen(true);
    };
    // Upcoming Maintenance → "Mark as Completed": prefill the known details and jump straight
    // to the editable Review step. The maintenance item is only marked completed once the
    // service record actually saves (the saved record advances the schedule) — nothing is
    // written while reviewing.
    const openServiceReviewForMaintenance = (item, vehicleIdArg) => {
        const vehicleId = vehicleIdArg ?? activeVehicle?.vehicle_id;
        if (!item?.key || !vehicleId) return;
        const vehicle = (garageData?.vehicles || []).find((v) => String(v?.vehicle_id) === String(vehicleId)) || activeVehicle;
        const current = getVehicleMileageValue(vehicle);
        const currentNum = current != null ? Number(String(current).replace(/,/g, '')) : null;
        setLogServiceForm({
            vehicleId,
            title: item.title || '',
            date: new Date().toISOString().slice(0, 10),
            mileage: Number.isFinite(currentNum) && currentNum > 0 ? String(Math.round(currentNum)) : '',
            performedBy: 'shop',
            cost: '',
            shopName: '',
            notes: '',
            serviceKey: item.key,
        });
        setLogServiceStep('review');
        setLogServiceError('');
        setLogServiceWarning('');
        setLogServiceOpen(true);
    };
    const closeLogServiceModal = () => {
        if (logServiceSaving) return;
        setLogServiceOpen(false);
        setLogServiceForm(null);
        setLogServiceStep('edit');
        setLogServiceError('');
        setLogServiceWarning('');
    };
    const setLogField = (field, value) => setLogServiceForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    // Validate the entered values (no network). Returns an error string, or '' if valid.
    const validateServiceForm = (form) => {
        if (!form) return 'Something went wrong. Please reopen the form.';
        if (!String(form.title || '').trim()) return 'Enter what service was performed.';
        if (!form.date) return 'Pick the service date.';
        if (form.date > new Date().toISOString().slice(0, 10)) return 'Service history records work already done. Pick today or earlier.';
        const mileageRaw = String(form.mileage || '').replace(/,/g, '').trim();
        if (mileageRaw && !/^\d+$/.test(mileageRaw)) return 'Enter the mileage as a whole number.';
        return '';
    };
    // Edit step → Review step. Validates locally; never creates a record.
    const goToServiceReview = () => {
        const err = validateServiceForm(logServiceForm);
        if (err) { setLogServiceError(err); return; }
        setLogServiceError('');
        setLogServiceWarning('');
        setLogServiceStep('review');
    };
    const handleSaveServiceRecord = async (confirmWarnings = false) => {
        const form = logServiceForm;
        if (!form || !authState.identity) return;
        const title = String(form.title || '').trim();
        if (!title) { setLogServiceError('Enter what service was performed.'); return; }
        if (!form.date) { setLogServiceError('Pick the service date.'); return; }
        const mileageRaw = String(form.mileage || '').replace(/,/g, '').trim();
        if (mileageRaw && !/^\d+$/.test(mileageRaw)) { setLogServiceError('Enter the mileage as a whole number.'); return; }
        const costRaw = String(form.cost || '').replace(/[^\d.]/g, '').trim();
        setLogServiceSaving(true);
        setLogServiceError('');
        try {
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/service-history/${encodeURIComponent(authState.identity)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    vehicle_id: form.vehicleId,
                    title,
                    performed_at: form.date,
                    odometer: mileageRaw ? Number(mileageRaw) : undefined,
                    odometer_unknown: mileageRaw ? undefined : true,
                    odometer_unit: 'mi',
                    performed_by: form.performedBy,
                    invoice_total: costRaw ? Number(costRaw) : undefined,
                    shop_name: String(form.shopName || '').trim() || undefined,
                    notes: String(form.notes || '').trim() || undefined,
                    confirm_warnings: confirmWarnings || undefined,
                }),
            });
            const data = await resp.json().catch(() => ({}));
            if (data?.status === 'confirm_required') {
                setLogServiceWarning((data.warnings || []).map((w) => w.message).filter(Boolean).join(' ') || 'Please confirm the mileage looks right.');
                return;
            }
            if (!resp.ok || data?.status === 'error' || data?.error) {
                throw new Error(data?.detail || data?.error || data?.message || 'Could not save the service record. Please try again.');
            }
            const targetVehicle = (garageData?.vehicles || []).find((v) => String(v?.vehicle_id) === String(form.vehicleId));
            setLogServiceOpen(false);
            setLogServiceForm(null);
            setLogServiceStep('edit');
            setLogServiceWarning('');
            setGarageUploadFeedback({
                message: data?.duplicate
                    ? 'That service was already in Service History.'
                    : 'Service record saved to Service History.',
            });
            await fetchGarageData();
            if (targetVehicle) await fetchMaintenanceForVehicles([targetVehicle]);
            if (String(form.vehicleId) === String(activeVehicle?.vehicle_id)) await fetchMaintenanceSchedule(form.vehicleId);
            try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
            // This fix may resolve open diagnostic cases for the same vehicle (often several
            // re-phrasings of the same complaint). Offer to close them — never auto-close.
            const matchVehicle = targetVehicle
                || (garageData?.vehicles || []).find((v) => String(v?.vehicle_id) === String(form.vehicleId))
                || null;
            const matchedCases = findOpenCasesResolvedByService(
                title,
                recentDiagnostics || [],
                matchVehicle,
                matchVehicle ? getVehicleLabel(matchVehicle) : ''
            );
            if (matchedCases.length > 0) {
                setCloseCasesOffer({ serviceTitle: title, tickets: matchedCases });
            }
        } catch (err) {
            setLogServiceError(err?.message || 'Could not save the service record. Please try again.');
        } finally {
            setLogServiceSaving(false);
        }
    };

    // Close every open case the driver confirmed the logged service resolves. Uses the same
    // PATCH-close endpoint as "Mark resolved", then refreshes so Active Work, the sidebar and the
    // chat header all drop the closed cases together.
    const confirmCloseMatchedCases = async () => {
        if (!closeCasesOffer || !authState.identity) { setCloseCasesOffer(null); return; }
        setClosingMatchedCases(true);
        const summary = `Resolved by logged service: ${closeCasesOffer.serviceTitle}`;
        try {
            await Promise.all((closeCasesOffer.tickets || []).map(async (ticket) => {
                const realId = ticketRealId(ticket);
                if (!realId) return;
                try {
                    await fetch(
                        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}/${encodeURIComponent(realId)}`,
                        {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ status: 'closed', stage: 'closed', outcome: 'successful', summary }),
                        }
                    );
                } catch (e) {
                    console.warn('[CloseMatchedCases] failed for ticket', realId, e);
                }
            }));
        } finally {
            setClosingMatchedCases(false);
            setCloseCasesOffer(null);
            refreshDiagnosticTickets?.();
            await fetchGarageData();
        }
    };

    // "Add document" / "Add photo": open the shared hidden file input, remembering which vehicle
    // and kind so handleGarageFileSelected uploads it to that vehicle's Glove Box.
    const handleAddDocumentForVehicle = (vehicle) => {
        if (!vehicle?.vehicle_id) return;
        setGarageUploadFeedback(null);
        setAddDocVehicleId(vehicle.vehicle_id);
        setAddDocOpen(true);
    };
    // Save a document from the Add Document wizard. With a real file -> multipart upload (file
    // + metadata); without -> a metadata-only reference record. Both land in the vehicle's Glove Box.
    const handleSaveDocument = async (payload) => {
        if (!authState.identity || !payload?.vehicleId) return;
        if (payload.file) {
            const body = new FormData();
            body.append('file', payload.file);
            body.append('vehicle_id', payload.vehicleId);
            body.append('document_kind', payload.documentKind || 'other');
            if (payload.title) body.append('title', payload.title);
            if (payload.notes) body.append('notes', payload.notes);
            if (payload.provider) body.append('provider', payload.provider);
            if (payload.reference) body.append('reference_no', payload.reference);
            if (payload.issued) body.append('issued_at', payload.issued);
            if (payload.expires) body.append('expires_at', payload.expires);
            if (payload.linkedCaseId) body.append('diagnostic_ticket_id', payload.linkedCaseId);
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me/upload`, { method: 'POST', credentials: 'include', body });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || data.error || data.status !== 'success') {
                throw new Error(typeof data.detail === 'string' ? data.detail : data.error || 'Could not save the document.');
            }
        } else {
            // Reference-only record (no file): store title + metadata + optional linked case.
            const markers = {};
            if (payload.provider) markers.provider = payload.provider;
            if (payload.reference) markers.reference = payload.reference;
            if (payload.issued) markers.issued_at = payload.issued;
            if (payload.expires) markers.expires_at = payload.expires;
            const uniqueName = `${Date.now()}_${(payload.filename || payload.title).replace(/[^\w.-]+/g, '_')}`;
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/documents/upsert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone_number: authState.identity,
                    name: uniqueName,
                    original_filename: payload.title,
                    path: '',
                    storage_url: '',
                    content: payload.notes || '',
                    source: 'user_upload',
                    vehicle_id: payload.vehicleId,
                    media_kind: payload.documentKind === 'photo' ? 'photo' : 'document',
                    document_kind: payload.documentKind || 'other',
                    extracted_markers: Object.keys(markers).length ? markers : undefined,
                    diagnostic_ticket_id: payload.linkedCaseId || undefined,
                    extraction_status: 'skipped',
                    upload_state: 'saved',
                }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || data.status === 'error' || data.error) {
                throw new Error(data.error || data.detail || 'Could not save the document.');
            }
        }
        setAddDocOpen(false);
        setGarageUploadFeedback({ message: 'Document added to the Glove Box.' });
        try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
        fetchDocumentCounts?.();
    };
    const handleAddPhotoForVehicle = (vehicle) => {
        if (!vehicle?.vehicle_id) return;
        setGarageUploadFeedback(null);
        garageUploadTargetRef.current = { vehicleId: vehicle.vehicle_id, kind: 'photo' };
        if (garageFileInputRef.current) {
            garageFileInputRef.current.accept = 'image/*';
            garageFileInputRef.current.click();
        }
    };
    // Remove the persistent vehicle photo (and clear any session preview).
    const handleRemovePhotoForVehicle = async (vehicle) => {
        const vid = vehicle?.vehicle_id;
        if (!vid) return;
        try {
            await removeVehiclePhoto(vid);
            setVehiclePhotos((prev) => {
                const url = prev[vid];
                if (url) { try { URL.revokeObjectURL(url); } catch (_) { /* noop */ } }
                const next = { ...prev };
                delete next[vid];
                return next;
            });
            setGarageUploadFeedback({ message: 'Vehicle photo removed.' });
            await fetchGarageData();
            try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
        } catch (error) {
            setGarageUploadFeedback({ error: true, message: error.message || 'Could not remove the photo. Please try again.' });
        }
    };
    const handleGarageFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        const target = garageUploadTargetRef.current;
        if (!file || !target?.vehicleId) return;

        // A vehicle photo is a dedicated, persistent image on the vehicle (shown on its card),
        // separate from Glove Box documents / diagnostic evidence. Re-encoded server-side.
        if (target.kind === 'photo') {
            if (!file.type?.startsWith('image/')) {
                setGarageUploadFeedback({ error: true, message: 'Choose an image file (JPG, PNG, WebP, or HEIC).' });
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setGarageUploadFeedback({ error: true, message: 'Vehicle photos must be 5 MB or smaller.' });
                return;
            }
            // Optimistic preview while the upload + re-encode happens.
            let previewUrl = '';
            try {
                previewUrl = URL.createObjectURL(file);
                setVehiclePhotos((prev) => ({ ...prev, [target.vehicleId]: previewUrl }));
            } catch (_) { /* noop */ }
            try {
                await uploadVehiclePhoto(target.vehicleId, file);
                setGarageUploadFeedback({ message: 'Vehicle photo saved.' });
                await fetchGarageData(); // picks up the persisted (presigned) photo_url
                // Drop the optimistic preview now that the persisted photo is in place.
                setVehiclePhotos((prev) => {
                    if (!prev[target.vehicleId]) return prev;
                    try { URL.revokeObjectURL(prev[target.vehicleId]); } catch (_) { /* noop */ }
                    const next = { ...prev };
                    delete next[target.vehicleId];
                    return next;
                });
                try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
            } catch (error) {
                setVehiclePhotos((prev) => {
                    if (prev[target.vehicleId] !== previewUrl) return prev;
                    try { URL.revokeObjectURL(previewUrl); } catch (_) { /* noop */ }
                    const next = { ...prev };
                    delete next[target.vehicleId];
                    return next;
                });
                setGarageUploadFeedback({ error: true, message: error.message || 'Could not save the photo. Please try again.' });
            }
            return;
        }

        // Documents → Glove Box (unchanged).
        if (file.size > 50 * 1024 * 1024) {
            setGarageUploadFeedback({ error: true, message: 'Please choose a file smaller than 50 MB.' });
            return;
        }
        try {
            const body = new FormData();
            body.append('file', file);
            body.append('vehicle_id', target.vehicleId);
            body.append('document_kind', 'other');
            const resp = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me/upload`, {
                method: 'POST', credentials: 'include', body,
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || data.error || data.status !== 'success') {
                throw new Error(typeof data.detail === 'string' ? data.detail : data.error || 'Could not upload. Please try again.');
            }
            setGarageUploadFeedback({
                message: data.duplicate
                    ? 'That file is already in this vehicle’s Glove Box.'
                    : 'Document added to the Glove Box.',
            });
            try { window.dispatchEvent(new CustomEvent('dash_vehicle_updated')); } catch { /* noop */ }
            fetchDocumentCounts?.();
        } catch (error) {
            setGarageUploadFeedback({ error: true, message: error.message || 'Could not upload. Please try again.' });
        }
    };

    // Open the chat thread tied to a diagnostic ticket so the user can continue
    // or ask a follow-up. Falls back to a seeded prompt when no thread is linked.
    const handleOpenTicketThread = (ticket) => {
        const threadId = ticketThreadId(ticket);
        if (threadId && onSelectThread) {
            onSelectThread(threadId);
            navigate('/chat');
            return;
        }
        if (onStartNewChat) onStartNewChat();
        navigate('/chat', { state: { q: `Continue diagnosing: ${ticketTitle(ticket)}` } });
    };

    // View Report: if a generated report for this ticket is cached locally, open
    // the report page; otherwise open the ticket's chat thread (the report card
    // lives inline there).
    const handleViewTicketReport = async (ticket) => {
        // Read the freshest cache from localStorage (not the possibly-stale React state) —
        // a report generated moments ago in chat may not be in state yet, which made
        // "View Report" intermittently fall through to the chat thread instead of the report.
        const freshReports = loadStoredDiagnosticReports();
        setStoredDiagnosticReports(freshReports);
        const match = findStoredReportForTicket(freshReports, ticket);
        if (match?.id) {
            saveDiagnosticReport(match);
            navigate(`/diagnostic-report/${encodeURIComponent(match.id)}`, { state: { report: match } });
            return;
        }
        // Not cached on THIS device (e.g. generated elsewhere or storage cleared). The report
        // page loads/generates the report itself from the backend when given an id + threadId,
        // so route there with a seed rather than dropping the user into the chat thread.
        const threadId = ticketThreadId(ticket);
        const ticketId = ticketRealId(ticket);
        const seedId = threadId
            ? `thread-${threadId}-diagnostic-report`
            : (ticketId ? `ticket-${ticketId}-diagnostic-report` : '');

        // Pre-fetch so we can open the report already populated (best UX). A short timeout
        // means a slow/hung backend can't leave the button spinning — we fall through to the
        // seed navigation below and let the report page keep trying with its own skeleton.
        if (threadId && authState?.identity && !viewingReportTicketId) {
            setViewingReportTicketId(ticketId || threadId);
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 8000);
            try {
                const resp = await fetch(
                    `${getApiBaseUrl()}/api/chat/secure/threads/${encodeURIComponent(threadId)}/diagnostic-report`,
                    {
                        method: 'POST',
                        credentials: 'include',
                        signal: controller.signal,
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            vehicle_label: getVehicleLabel(activeVehicle) || null,
                            report_id: seedId,
                        }),
                    },
                );
                const data = await resp.json().catch(() => ({}));
                const report = data?.report;
                if (resp.ok) announceTicketTitle(threadId, data);
                if (resp.ok && report && typeof report === 'object' && Object.keys(report).length > 0) {
                    const normalized = report.id ? report : { ...report, id: seedId, threadId };
                    saveDiagnosticReport(normalized);
                    navigate(`/diagnostic-report/${encodeURIComponent(normalized.id)}`, { state: { report: normalized } });
                    return;
                }
            } catch (err) {
                console.warn('[garage] view report pre-fetch failed, opening report page to retry:', err);
            } finally {
                clearTimeout(timer);
                setViewingReportTicketId(null);
            }
        }

        // Open the report page with a seed so it fetches/generates the report itself — never
        // silently drop to the chat when we can identify the thread.
        if (seedId && threadId) {
            const seed = {
                id: seedId,
                threadId,
                diagnosticTicketId: ticketId || undefined,
                vehicle: getVehicleLabel(activeVehicle) || undefined,
                issue: ticketTitle(ticket) || undefined,
            };
            saveDiagnosticReport(seed);
            navigate(`/diagnostic-report/${encodeURIComponent(seedId)}`, { state: { report: seed } });
            return;
        }

        // Only when there is no thread to load a report from — open the chat thread.
        handleOpenTicketThread(ticket);
    };

    const handleOpenEstimateDrawer = (ticket) => {
        setStoredDiagnosticReports(loadStoredDiagnosticReports());
        setShopDrawerTicket(ticket);
    };

    // "Find shop" from a vehicle's upcoming-maintenance selection. Opens the shop drawer in
    // maintenance mode immediately, then creates a lightweight estimate ticket in the
    // background so "Request Estimate" becomes available (falls back to contact-only if that
    // fails). Maintenance reports have no diagnostic ticket of their own.
    const handleFindShopForMaintenance = async (vehicle, items) => {
        const selected = Array.isArray(items) ? items.filter(Boolean) : [];
        if (!vehicle || selected.length === 0) return;
        const vehicleLabel = getVehicleLabel(vehicle) || activeVehicleLabel;
        const titles = selected.map((it) => it.title).filter(Boolean);
        setMaintShopDrawer({ vehicle, vehicleLabel, items: selected, diagnosticTicketId: null, creating: true });
        try {
            const title = titles.join(', ') || 'Scheduled maintenance';
            const { diagnosticTicketId } = await createMaintenanceEstimateTicket({
                title,
                summary: `Scheduled maintenance: ${titles.join(', ')}`,
                vehicleId: vehicle?.vehicle_id ?? null,
            });
            setMaintShopDrawer((prev) => (prev && String(prev.vehicle?.vehicle_id) === String(vehicle?.vehicle_id)
                ? { ...prev, diagnosticTicketId: diagnosticTicketId || null, creating: false }
                : prev));
        } catch (err) {
            console.warn('Maintenance estimate ticket creation failed; opening contact-only shops:', err);
            setMaintShopDrawer((prev) => (prev && String(prev.vehicle?.vehicle_id) === String(vehicle?.vehicle_id)
                ? { ...prev, creating: false }
                : prev));
        }
    };

    const openGarageRequestInDash = (request) => {
        if (!request?.request_id) return;
        const directThreadId = (
            request?.source_repair_log_id ||
            request?.sourceRepairLogId ||
            request?.repair_log_id ||
            request?.repairLogId ||
            request?.thread_id ||
            request?.threadId ||
            ''
        );
        const matchedTicket = (recentDiagnostics || []).find((ticket) => (
            String(ticketRealId(ticket) || '') === String(request?.diagnostic_ticket_id || request?.diagnosticTicketId || '') ||
            (directThreadId && String(ticketThreadId(ticket) || '') === String(directThreadId))
        ));
        const linkedThreadId = String(directThreadId || ticketThreadId(matchedTicket) || '');
        if (linkedThreadId && onSelectThread) {
            onSelectThread(linkedThreadId);
        }
        // Pass the full request so the chat can open the shop pane even when the request's
        // ticket isn't the active diagnostic thread (e.g. a maintenance estimate).
        navigate('/chat', { state: { openGarageRequestId: request.request_id, openGarageRequest: request } });
    };

    const handleGarageRequestCreated = (request) => {
        if (!request?.request_id) return;
        setGarageShopRequests((prev) => {
            const exists = prev.some((item) => item.request_id === request.request_id);
            return exists
                ? prev.map((item) => (item.request_id === request.request_id ? request : item))
                : [request, ...prev];
        });
        consumedShopIntentRef.current = '';
        setShopDrawerTicket(null);
        setMaintShopDrawer(null);
        openGarageRequestInDash(request);
    };

    const handleOpenGarageChat = (request) => {
        openGarageRequestInDash(request);
    };

    // Open the confirmation step instead of closing immediately, so the driver can
    // verify the work was done and add anything worth keeping in service history.
    const handleMarkTicketResolved = (ticket) => {
        const realId = ticketRealId(ticket);
        if (!realId || !authState.identity) return;
        // Prefill the odometer from the ticket's vehicle (fall back to the active one).
        const ticketVehicle = vehicles.find((v) => ticketBelongsToVehicle(ticket, getVehicleLabel(v)) || String(v.vehicle_id) === String(ticket?.vehicle_id)) || activeVehicle;
        const currentOdo = getVehicleMileageValue(ticketVehicle);
        setResolveOdometer(currentOdo != null ? String(currentOdo) : '');
        setResolveServicePerformed(true);
        setResolveNotes('');
        setResolveError('');
        setResolveTicket(ticket);
    };

    const handleConfirmResolve = async () => {
        const ticket = resolveTicket;
        const realId = ticket && ticketRealId(ticket);
        if (!realId || !authState.identity) return;
        // The reading is entered in the user's preferred unit; store canonical MILES.
        const odoNum = toMiles(resolveOdometer, distanceUnit);
        // Enforce the 1,000,000-mile maximum (#412) before closing/saving.
        if (odoNum !== null && odoNum > MAX_ODOMETER_MILES) {
            setResolveError(ODOMETER_TOO_HIGH_MESSAGE);
            return;
        }
        setResolveError('');
        setResolvingTicketId(realId);
        try {
            const resolveResponse = await fetch(`${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(authState.identity)}/${encodeURIComponent(realId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: 'closed',
                    stage: 'closed',
                    outcome: 'successful',
                    summary: resolveServicePerformed
                        ? 'Marked resolved from the Garage. Service confirmed performed.'
                        : 'Marked resolved from the Garage.',
                    // Only write to service history when the driver confirms the work was done.
                    service_performed: resolveServicePerformed,
                    service_title: resolveServicePerformed ? ticketTitle(ticket) : undefined,
                    service_notes: resolveServicePerformed ? (resolveNotes.trim() || undefined) : undefined,
                    service_odometer: resolveServicePerformed && odoNum != null ? odoNum : undefined,
                }),
            });
            if (!resolveResponse.ok) {
                const errorData = await resolveResponse.json().catch(() => ({}));
                throw new Error(errorData?.detail || errorData?.error || 'Unable to resolve ticket');
            }
            const updatedTicket = await resolveResponse.json();
            window.dispatchEvent(new CustomEvent('dash_active_diagnostic_ticket', { detail: updatedTicket }));
            // Sync the entered odometer to the vehicle so the Garage and its
            // maintenance schedule reflect the latest reading — matching the guided
            // repair-log flow in chat, which also updates the odometer on close.
            const resolveVehicleId = (
                ticket?.vehicle_id ||
                ticket?.vehicle?.vehicle_id ||
                (vehicles.find((v) => ticketBelongsToVehicle(ticket, getVehicleLabel(v))) || activeVehicle)?.vehicle_id ||
                null
            );
            if (resolveServicePerformed && odoNum != null && odoNum > 0 && resolveVehicleId) {
                try {
                    await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            phone_number: authState.identity,
                            vehicle_id: resolveVehicleId,
                            is_new_vehicle: false,
                            odometer_reading: odoNum,
                            mileage: odoNum,
                        }),
                    });
                    window.dispatchEvent(new CustomEvent('dash_vehicle_updated'));
                } catch (odoErr) {
                    console.warn('Odometer sync from resolve failed:', odoErr);
                }
            }
            refreshDiagnosticTickets?.();
            await fetchGarageData();
            setResolveTicket(null);
        } catch (err) {
            console.error('Mark resolved failed:', err);
        } finally {
            setResolvingTicketId(null);
        }
    };

    const handleDismissTicket = (ticket) => {
        const realId = ticketRealId(ticket) || ticketThreadId(ticket);
        if (!realId) return;
        setDismissedTicketIds((prev) => {
            const next = new Set(prev);
            next.add(String(realId));
            writeLocalStringSet(dismissedStorageKey, next);
            return next;
        });
    };

    const shopDrawerDiagnosticTicketId = getGarageDiagnosticTicketId(shopDrawerTicket);
    const shopDrawerReportSnapshot = findStoredReportForTicket(storedDiagnosticReports, shopDrawerTicket);
    const shopDrawerSentShopIds = shopDrawerDiagnosticTicketId
        ? garageShopRequests
            .filter((request) => String(request?.diagnostic_ticket_id || '') === String(shopDrawerDiagnosticTicketId))
            .map((request) => request?.partner_shop?.partner_shop_id || request?.partner_shop_id)
            .filter(Boolean)
        : [];

    // Shop drawer is shared between the diagnostic (ticket) flow and the maintenance
    // (selected-items) flow — maintenance takes precedence when its state is set.
    const shopDrawerTicketId = maintShopDrawer ? maintShopDrawer.diagnosticTicketId : shopDrawerDiagnosticTicketId;
    const shopDrawerMaintSentIds = maintShopDrawer && maintShopDrawer.diagnosticTicketId
        ? garageShopRequests
            .filter((request) => String(request?.diagnostic_ticket_id || '') === String(maintShopDrawer.diagnosticTicketId))
            .map((request) => request?.partner_shop?.partner_shop_id || request?.partner_shop_id)
            .filter(Boolean)
        : [];

    return (
        <div className="h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar relative">
            <div className="mx-auto w-full max-w-3xl px-4 pt-8 pb-12 md:px-6 md:pt-10">
                <header className="mb-8 flex flex-col gap-5 animate-in fade-in slide-in-from-top-4 duration-700">
                    <div>
                        <h1 className="text-2xl md:text-[28px] font-black text-black tracking-tight">
                            Garage
                        </h1>
                        <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">
                            Vehicle command center
                        </p>
                    </div>
                    
                    <div className="flex w-full flex-wrap items-center gap-3 animate-in fade-in duration-700">
                        <button
                            onClick={handleStartDiagnostic}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-black shadow-sm transition-all hover:border-[#414141] hover:bg-[#F9F9F9]"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                            </svg>
                            Start Diagnostic
                        </button>
                        <button 
                            onClick={handleAddVehicle}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#D81F26] px-5 text-sm font-black text-white shadow-sm transition-all hover:bg-[#B8181E] sm:ml-auto"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12h14M12 5v14"/></svg>
                            Add Vehicle
                        </button>
                    </div>
                </header>

                {error && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 font-bold mb-6 flex items-center gap-4">
                        {error}
                    </div>
                )}

                {vehicles.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#d9d9d9] bg-white px-6 py-10 text-center shadow-sm">
                        <div className="w-16 h-16 bg-[#F9F9F9] rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm border border-[#d9d9d9]">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#D81F26]">
                                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>
                        </div>
                        <h3 className="text-lg font-black text-black mb-2">Your garage is empty</h3>
                        <p className="text-sm text-[#414141] font-medium mb-6 max-w-sm mx-auto">Start a chat with CarFido to add your vehicle and begin tracking its health.</p>
                        <button
                            onClick={handleAddVehicle}
                            className="inline-flex min-h-[42px] items-center gap-2 px-6 bg-black text-white rounded-full text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-lg"
                        >
                            Add First Vehicle
                        </button>
                    </div>
                ) : (
                    <div>
                        {/* Attention banner: vehicles with open diagnostics. */}
                        {attentionCount > 0 && !attentionDismissed && (
                            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#d8e6f3] bg-white px-4 py-3.5 shadow-sm">
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef6fc] text-[#0b74b5]">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
                                </span>
                                <p className="flex-1 text-sm font-semibold text-[#1f3350]">
                                    <span className="font-black text-[#0b1b2e]">
                                        {attentionCount} item{attentionCount === 1 ? '' : 's'} need attention
                                    </span>
                                    {attentionVehicles.map((c) => (
                                        <span key={c.vehicle.vehicle_id}>
                                            {' · '}
                                            <button
                                                type="button"
                                                onClick={() => handleOpenTicketThread(c.openTickets[0])}
                                                className="font-black text-[#0b74b5] underline-offset-2 hover:underline"
                                            >
                                                {[c.vehicle.make, c.vehicle.model].filter(Boolean).join(' ') || getVehicleLabel(c.vehicle)}: {c.openTickets.length} diagnostic{c.openTickets.length === 1 ? '' : 's'}
                                            </button>
                                        </span>
                                    ))}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setAttentionDismissed(true)}
                                    aria-label="Dismiss"
                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#5f728a] transition hover:bg-[#f2f9ff]"
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                        )}

                        {garageUploadFeedback && (
                            <div className={`mb-6 rounded-2xl border px-4 py-3 text-sm font-bold ${
                                garageUploadFeedback.error
                                    ? 'border-red-100 bg-red-50 text-red-600'
                                    : 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]'
                            }`}>
                                {garageUploadFeedback.message}
                            </div>
                        )}

                        {/* Per-vehicle cards (two-column). */}
                        <div className="grid gap-5 lg:grid-cols-2">
                            {vehicleCards.map(({ vehicle, openTickets, maint }) => (
                                <GarageVehicleCard
                                    key={vehicle.vehicle_id}
                                    vehicle={vehicle}
                                    isActive={String(vehicle.vehicle_id) === String(activeVehicle?.vehicle_id)}
                                    mileageText={formatVehicleMileage(getVehicleMileageValue(vehicle), distanceUnit)}
                                    photoUrl={vehicle.photo_url || vehiclePhotos[vehicle.vehicle_id]}
                                    openTickets={openTickets}
                                    maintenance={maint}
                                    onSetActive={handleSwitchVehicle}
                                    onUpdateOdometer={openOdometerModal}
                                    onLogService={openLogServiceModal}
                                    onAddDocument={handleAddDocumentForVehicle}
                                    onAddPhoto={handleAddPhotoForVehicle}
                                    onRemovePhoto={handleRemovePhotoForVehicle}
                                    onEditVehicle={handleEditVehicle}
                                    onOpenTicket={handleOpenTicketThread}
                                    onMaintenanceConfirm={handleMaintenanceConfirm}
                                    onMaintenanceComplete={openServiceReviewForMaintenance}
                                    onFetchWhy={(serviceKey) => fetchMaintenanceWhy(vehicle.vehicle_id, serviceKey)}
                                    onFindShop={handleFindShopForMaintenance}
                                    onOpenDetail={(v) => setDetailVehicleId(v.vehicle_id)}
                                />
                            ))}
                        </div>

                        <ToolInventorySection
                            tools={tools}
                            onAdd={handleAddTool}
                            onRemove={handleRemoveTool}
                            isLoading={toolsLoading}
                        />
                    </div>
                )}
            </div>

            {/* Shared hidden file input for the card "Add photo" tile. */}
            <input
                ref={garageFileInputRef}
                type="file"
                className="hidden"
                onChange={handleGarageFileSelected}
            />

            <AddDocumentWizard
                isOpen={addDocOpen}
                onClose={() => setAddDocOpen(false)}
                vehicles={vehicles}
                initialVehicleId={addDocVehicleId}
                resolvedCases={resolvedCases}
                onSave={handleSaveDocument}
            />

            <VehicleDetailDrawer
                vehicle={detailVehicle}
                isOpen={Boolean(detailVehicle)}
                onClose={() => setDetailVehicleId(null)}
                distanceUnit={distanceUnit}
                phone={authState.identity}
                isActive={String(detailVehicleId) === String(activeVehicle?.vehicle_id)}
                openCount={detailDiagnostics.filter((d) => d.action === 'continue').length}
                photoUrl={detailVehicle?.photo_url || vehiclePhotos[detailVehicleId]}
                diagnostics={detailDiagnostics}
                maintenanceItems={detailMaintItems}
                chats={detailChats}
                onSetActive={handleSwitchVehicle}
                onStartDiagnostic={async (v) => {
                    if (v?.vehicle_id && String(v.vehicle_id) !== String(activeVehicle?.vehicle_id)) {
                        try { await handleSwitchVehicle(v); } catch (_) { /* best-effort */ }
                    }
                    setDetailVehicleId(null);
                    handleStartDiagnostic();
                }}
                onAddPhoto={handleAddPhotoForVehicle}
                onLogService={(v) => { setDetailVehicleId(null); openLogServiceModal(v); }}
                onAddDocument={(v) => { setDetailVehicleId(null); handleAddDocumentForVehicle(v); }}
                onEditVehicle={(v) => { setDetailVehicleId(null); handleEditVehicle(v); }}
                onOpenTicket={(t) => { setDetailVehicleId(null); handleOpenTicketThread(t); }}
                onViewReport={(t) => { setDetailVehicleId(null); handleViewTicketReport(t); }}
                onOpenChat={(raw) => { setDetailVehicleId(null); handleOpenDetailChat(raw); }}
                onOpenDocument={handleOpenDetailDocument}
            />

            {/* Update odometer modal */}
            {odometerModalVehicle && (
                <div
                    className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 px-4 py-4 sm:items-center"
                    onClick={closeOdometerModal}
                >
                    <div
                        className="w-full max-w-md overflow-hidden rounded-2xl border border-[#d8e6f3] bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 pt-5">
                            <div className="flex items-start justify-between gap-3">
                                <h2 className="text-lg font-black text-[#0b1b2e]">Update odometer</h2>
                                <button
                                    type="button"
                                    onClick={closeOdometerModal}
                                    disabled={odometerModalSaving}
                                    aria-label="Close"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#5f728a] transition hover:bg-[#f2f9ff] disabled:opacity-60"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <p className="mt-1.5 text-sm font-semibold leading-relaxed text-[#5f728a]">
                                {getVehicleLabel(odometerModalVehicle)}{odometerModalVehicle.trim ? ` ${odometerModalVehicle.trim}` : ''}. Changes apply to this vehicle only; its photo and records stay attached.
                            </p>
                        </div>
                        <div className="px-6 pt-5">
                            <label htmlFor="garage-odometer-input" className="text-xs font-black uppercase tracking-[0.08em] text-[#42566c]">
                                Odometer (miles) <span className="text-[#b42318]">*</span>
                            </label>
                            <input
                                id="garage-odometer-input"
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={odometerModalValue}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => { setOdometerModalValue(e.target.value.replace(/[^\d,]/g, '')); setOdometerModalError(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !odometerModalSaving) handleSaveOdometer(); }}
                                placeholder="e.g. 60000"
                                className="mt-2 w-full rounded-xl border border-[#f7d58f] bg-white px-4 py-3 text-base font-bold text-[#182233] outline-none transition focus:border-[#0b74b5] focus:ring-2 focus:ring-[#b9ddf8]"
                            />
                            {odometerModalError && (
                                <p className="mt-2 text-sm font-bold text-[#b42318]">{odometerModalError}</p>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-5">
                            <button
                                type="button"
                                onClick={closeOdometerModal}
                                disabled={odometerModalSaving}
                                className="rounded-xl border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveOdometer}
                                disabled={odometerModalSaving}
                                className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#095f90] disabled:cursor-wait disabled:opacity-60"
                            >
                                {odometerModalSaving ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Log a Service modal */}
            {logServiceOpen && logServiceForm && (
                <div
                    className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 px-4 py-4 sm:items-center"
                    onClick={closeLogServiceModal}
                >
                    <div
                        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-[#d8e6f3] bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="shrink-0 px-6 pt-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-[#0b74b5]">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" /></svg>
                                    </span>
                                    <h2 className="text-lg font-black text-[#0b1b2e]">
                                        {logServiceStep === 'review' ? 'Review Service' : 'Log a Service'}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeLogServiceModal}
                                    disabled={logServiceSaving}
                                    aria-label="Close"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#5f728a] transition hover:bg-[#f2f9ff] disabled:opacity-60"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <p className="mt-1.5 text-sm font-semibold text-[#5f728a]">
                                {logServiceStep === 'review'
                                    ? 'Check the details below, then confirm. Nothing is saved until you confirm.'
                                    : "Record maintenance or repairs you've already completed."}
                            </p>
                        </div>

                        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Vehicle</label>
                                {logServiceStep === 'review' ? (
                                    <p className="mt-1.5 w-full rounded-xl border border-[#e5eef6] bg-[#f8fbfe] px-3.5 py-2.5 text-sm font-black text-[#182233]">
                                        {(() => {
                                            const v = vehicles.find((x) => String(x.vehicle_id) === String(logServiceForm.vehicleId));
                                            return v ? `${getVehicleLabel(v)}${v.trim ? ` ${v.trim}` : ''}` : 'This vehicle';
                                        })()}
                                    </p>
                                ) : (
                                    <select
                                        value={logServiceForm.vehicleId}
                                        onChange={(e) => setLogField('vehicleId', e.target.value)}
                                        className="mt-1.5 w-full rounded-xl border border-[#f7d58f] bg-white px-3.5 py-2.5 text-sm font-bold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                    >
                                        {vehicles.map((v) => (
                                            <option key={v.vehicle_id} value={v.vehicle_id}>
                                                {getVehicleLabel(v)}{v.trim ? ` ${v.trim}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Service Performed</label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={logServiceForm.title}
                                    onChange={(e) => { setLogField('title', e.target.value); setLogServiceError(''); }}
                                    placeholder="e.g., Oil Change, Brake Pads"
                                    className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Date</label>
                                    <input
                                        type="date"
                                        value={logServiceForm.date}
                                        max={new Date().toISOString().slice(0, 10)}
                                        onChange={(e) => setLogField('date', e.target.value)}
                                        className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Mileage</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={logServiceForm.mileage}
                                        onChange={(e) => setLogField('mileage', e.target.value.replace(/[^\d,]/g, ''))}
                                        placeholder="e.g., 45000"
                                        className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Who performed it?</label>
                                    <select
                                        value={logServiceForm.performedBy}
                                        onChange={(e) => setLogField('performedBy', e.target.value)}
                                        className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                    >
                                        <option value="shop">Repair Shop</option>
                                        <option value="dealer">Dealer</option>
                                        <option value="DIY">Myself (DIY)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-black text-[#1f3350]">Cost (Optional)</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={logServiceForm.cost}
                                        onChange={(e) => setLogField('cost', e.target.value.replace(/[^\d.]/g, ''))}
                                        placeholder="0.00"
                                        className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Shop Name (Optional)</label>
                                <input
                                    type="text"
                                    value={logServiceForm.shopName}
                                    onChange={(e) => setLogField('shopName', e.target.value)}
                                    placeholder="e.g., Main St Auto"
                                    className="mt-1.5 w-full rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-black text-[#1f3350]">Notes (Optional)</label>
                                <textarea
                                    rows={3}
                                    value={logServiceForm.notes}
                                    onChange={(e) => setLogField('notes', e.target.value)}
                                    placeholder="Any extra details, part brands used, etc."
                                    className="mt-1.5 w-full resize-y rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#182233] outline-none transition focus:border-[#0b74b5]"
                                />
                            </div>

                            {logServiceWarning && (
                                <p className="rounded-xl border border-[#f7d58f] bg-[#fff8e8] px-3 py-2 text-sm font-bold text-[#936000]">{logServiceWarning}</p>
                            )}
                            {logServiceError && (
                                <p className="text-sm font-bold text-[#b42318]">{logServiceError}</p>
                            )}
                        </div>

                        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#edf3f8] px-6 py-4">
                            {logServiceStep === 'review' ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => { if (!logServiceSaving) { setLogServiceStep('edit'); setLogServiceWarning(''); setLogServiceError(''); } }}
                                        disabled={logServiceSaving}
                                        className="mr-auto inline-flex items-center gap-1.5 rounded-xl border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={closeLogServiceModal}
                                        disabled={logServiceSaving}
                                        className="rounded-xl border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSaveServiceRecord(Boolean(logServiceWarning))}
                                        disabled={logServiceSaving}
                                        className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#095f90] disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {logServiceSaving ? 'Saving…' : logServiceWarning ? 'Save anyway' : 'Confirm & Save'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={closeLogServiceModal}
                                        disabled={logServiceSaving}
                                        className="rounded-xl border border-[#d8e6f3] bg-white px-5 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f8fbfe] disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goToServiceReview}
                                        className="rounded-xl bg-[#0b74b5] px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#095f90]"
                                    >
                                        Review Service
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <VehicleFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialData={editingVehicle}
                onSave={handleVehicleSaved}
                onSaveAndLogService={handleSaveAndLogService}
                phone={authState.identity}
                vehicles={vehicles}
                distanceUnit={distanceUnit}
            />

            <PartnerShopDrawer
                open={Boolean(shopDrawerTicket) || Boolean(maintShopDrawer)}
                onClose={() => { consumedShopIntentRef.current = ''; setShopDrawerTicket(null); setMaintShopDrawer(null); }}
                diagnosticTicketId={shopDrawerTicketId}
                reportSnapshot={maintShopDrawer ? null : shopDrawerReportSnapshot}
                canRequestEstimate={Boolean(shopDrawerTicketId)}
                context={maintShopDrawer ? {
                    vehicleName: maintShopDrawer.vehicleLabel || activeVehicleLabel,
                    symptomSummary: maintShopDrawer.items.map((it) => it.title).filter(Boolean).join(', '),
                    defaultZip: profileZip || '',
                    searchRadiusMiles: profileShopSearchRadiusMiles,
                } : {
                    vehicleName: ticketVehicleLabel(shopDrawerTicket) || activeVehicleLabel,
                    symptomSummary: ticketTitle(shopDrawerTicket),
                    defaultZip: profileZip || shopDrawerTicket?.defaultZip || shopDrawerTicket?.default_zip || '',
                    searchRadiusMiles: profileShopSearchRadiusMiles,
                }}
                maintenanceItems={maintShopDrawer ? maintShopDrawer.items : null}
                sentShopIds={maintShopDrawer ? shopDrawerMaintSentIds : shopDrawerSentShopIds}
                onRequestCreated={handleGarageRequestCreated}
            />

            {/* Vehicle Detail Panel */}
            <VehicleDetailPanel
                car={selectedCar}
                workplans={selectedCar ? workplans.filter(wp => wp.vehicle_id === selectedCar.vehicle_id) : []}
                isOpen={!!selectedCar}
                onClose={() => setSelectedCar(null)}
                onEdit={() => { handleEditVehicle(selectedCar); setSelectedCar(null); }}
                onRemove={() => openRemoveConfirm(selectedCar)}
                distanceUnit={distanceUnit}
            />

            {/* Offer to close the open cases a just-logged service resolves */}
            {closeCasesOffer && (
                <div className="fixed inset-0 z-[115] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => { if (!closingMatchedCases) setCloseCasesOffer(null); }}
                    />
                    <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl p-7 animate-in fade-in zoom-in-95 duration-200">
                        <h3 className="text-lg font-black tracking-tight text-black">Close matching open cases?</h3>
                        <p className="mt-2 text-sm font-semibold text-[#6B6B6B]">
                            {closeCasesOffer.tickets.length === 1
                                ? 'This fix looks like it resolves 1 open case:'
                                : `This fix looks like it resolves ${closeCasesOffer.tickets.length} open cases:`}
                        </p>
                        <ul className="mt-3 max-h-48 space-y-1.5 overflow-y-auto">
                            {closeCasesOffer.tickets.map((ticket) => (
                                <li
                                    key={ticketRealId(ticket) || ticketThreadId(ticket)}
                                    className="flex items-start gap-2 rounded-xl border border-[#ececec] bg-[#FAFAFA] px-3 py-2 text-sm font-bold text-[#414141]"
                                >
                                    <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#D81F26]" aria-hidden="true" />
                                    <span className="min-w-0">{resolveTicketDisplayTitle(ticket, threadTitleById)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                disabled={closingMatchedCases}
                                onClick={() => setCloseCasesOffer(null)}
                                className="flex-1 rounded-xl border border-[#d9d9d9] bg-white py-3 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-50"
                            >
                                Not now
                            </button>
                            <button
                                type="button"
                                disabled={closingMatchedCases}
                                onClick={confirmCloseMatchedCases}
                                className="flex-1 rounded-xl bg-[#D81F26] py-3 text-sm font-black text-white transition hover:bg-[#B8181E] disabled:opacity-60"
                            >
                                {closingMatchedCases
                                    ? 'Closing…'
                                    : closeCasesOffer.tickets.length === 1 ? 'Close case' : `Close ${closeCasesOffer.tickets.length} cases`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove-vehicle confirmation */}
            {carToRemove && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => { if (!isRemoving) setCarToRemove(null); }}
                    />
                    <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl p-7 animate-in fade-in zoom-in-95 duration-200">
                        <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"/></svg>
                        </div>
                        <h3 className="text-xl font-black text-black tracking-tight mb-2">Remove this vehicle?</h3>
                        <p className="text-base font-black text-black tracking-tight mb-1">
                            {[carToRemove.year, carToRemove.make, carToRemove.model].filter(Boolean).join(' ')}
                        </p>
                        <p className="text-sm font-semibold text-black/50 leading-relaxed mb-6">
                            This removes it from your garage. Saved service history stays on file but won't show here.
                        </p>
                        {removeError && (
                            <p className="text-sm font-bold text-rose-600 mb-4">{removeError}</p>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setCarToRemove(null)}
                                disabled={isRemoving}
                                className="flex-1 py-3 rounded-full border border-black/10 font-black uppercase tracking-widest text-xs text-black/60 hover:bg-black/5 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemoveVehicle}
                                disabled={isRemoving}
                                className="flex-1 py-3 rounded-full bg-rose-600 text-white font-black uppercase tracking-widest text-xs hover:bg-rose-700 transition-colors disabled:opacity-60"
                            >
                                {isRemoving ? 'Removing…' : 'Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mark-resolved confirmation: verify the work before closing the ticket */}
            {resolveTicket && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => { if (!resolvingTicketId) setResolveTicket(null); }}
                    />
                    <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-2xl p-7 animate-in fade-in zoom-in-95 duration-200">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>
                        </div>
                        <h3 className="text-xl font-black text-black tracking-tight mb-1">Confirm before resolving</h3>
                        <p className="text-base font-black text-black tracking-tight mb-4">
                            {ticketTitle(resolveTicket)}
                        </p>

                        <p className="text-sm font-black text-black mb-2">Was the expected service performed?</p>
                        <div className="flex gap-2 mb-5">
                            <button
                                type="button"
                                onClick={() => setResolveServicePerformed(true)}
                                className={`flex-1 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${resolveServicePerformed ? 'bg-emerald-600 text-white' : 'border border-black/10 text-black/60 hover:bg-black/5'}`}
                            >
                                Yes, it was
                            </button>
                            <button
                                type="button"
                                onClick={() => setResolveServicePerformed(false)}
                                className={`flex-1 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${!resolveServicePerformed ? 'bg-black text-white' : 'border border-black/10 text-black/60 hover:bg-black/5'}`}
                            >
                                Not yet
                            </button>
                        </div>

                        {resolveServicePerformed ? (
                            <>
                                <label className="block text-sm font-black text-black mb-1.5">
                                    Add to service history <span className="font-semibold text-black/40">(optional)</span>
                                </label>
                                <textarea
                                    value={resolveNotes}
                                    onChange={(e) => setResolveNotes(e.target.value)}
                                    rows={3}
                                    placeholder="Parts replaced, work done, who did it…"
                                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm font-medium text-black placeholder:text-black/30 focus:border-[#D81F26] focus:outline-none resize-none mb-4"
                                />
                                <label className="block text-sm font-black text-black mb-1.5">
                                    Odometer at service <span className="font-semibold text-black/40">({distanceUnit}, optional)</span>
                                </label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={resolveOdometer}
                                    onChange={(e) => { setResolveOdometer(e.target.value); setResolveError(''); }}
                                    placeholder="e.g. 45000"
                                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm font-medium text-black placeholder:text-black/30 focus:border-[#D81F26] focus:outline-none mb-2"
                                />
                                {resolveError && <p className="text-sm font-bold text-rose-600 mb-4">{resolveError}</p>}
                            </>
                        ) : (
                            <p className="text-sm font-semibold text-black/50 leading-relaxed mb-6">
                                The ticket will be closed without adding a service-history record. You can reopen it from Dash if the issue comes back.
                            </p>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setResolveTicket(null)}
                                disabled={Boolean(resolvingTicketId)}
                                className="flex-1 py-3 rounded-full border border-black/10 font-black uppercase tracking-widest text-xs text-black/60 hover:bg-black/5 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmResolve}
                                disabled={Boolean(resolvingTicketId)}
                                className="flex-1 py-3 rounded-full bg-emerald-600 text-white font-black uppercase tracking-widest text-xs hover:bg-emerald-700 transition-colors disabled:opacity-60"
                            >
                                {resolvingTicketId ? 'Resolving…' : 'Confirm & Resolve'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Named exports for focused tests of the mobile-responsive sections.
export { ActiveWorkSection, UpcomingMaintenanceSection, VehicleDetailPanel, VehicleFormModal };
export default GaragePage;
